import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  RefreshCw,
  Target,
  Phone,
  Users,
  TrendingUp,
  AlertTriangle,
  Search,
  Activity,
  Zap,
  BarChart3,
  ChevronRight,
  Headphones,
  PhoneCall,
  Filter,
  CheckCircle,
  Flame,
  X,
  PhoneMissed,
  Trophy,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWtfLiveData } from '../hooks/useWtfLiveData';
import AutomationHealthPanel from '../components/pulse/AutomationHealthPanel';
import CustomerListDialog from '../components/closure/CustomerListDialog';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/** Indian Rupee formatting with lakhs/crores notation */
const formatINR = (amount) => {
  if (!amount && amount !== 0) return '--';
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '--';
  return '\u20B9' + num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

/** Normalize gym name for cross-feed matching */
const normalizeGymName = (name) => {
  if (!name) return '';
  return name.replace(/,/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
};

/** Safe numeric parse - handles strings, nulls, undefined */
const safeNum = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
};

/** Get achievement percentage */
const achievementPct = (achieved, target) => {
  const t = safeNum(target);
  const a = safeNum(achieved);
  if (t === 0) return a > 0 ? 100 : 0;
  return Math.round((a / t) * 100);
};

/** Row color class based on achievement percentage */
const achievementRowClass = (pct) => {
  if (pct >= 80) return 'bg-green-950/30 border-l-4 border-l-green-500';
  if (pct >= 50) return '';
  if (pct >= 10) return 'bg-amber-950/20 border-l-4 border-l-amber-500';
  return 'bg-red-950/30 border-l-4 border-l-red-500';
};

/** Achievement badge */
const AchievementBadge = ({ pct }) => {
  let className = 'text-xs font-bold px-2 py-0.5 rounded ';
  if (pct >= 80) className += 'bg-green-900/60 text-green-300 border border-green-700';
  else if (pct >= 50) className += 'bg-amber-900/60 text-amber-300 border border-amber-700';
  else className += 'bg-red-900/60 text-red-300 border border-red-700';
  return <span className={className}>{pct}%</span>;
};

/** Check if a date string is today */
/** Get today's date in IST (YYYY-MM-DD) to match backend which uses Asia/Kolkata */
const getISTDate = () => {
  const now = new Date();
  const istMs = now.getTime() + (5.5 * 60 * 60 * 1000); // UTC + 5:30
  return new Date(istMs).toISOString().split('T')[0];
};
const isToday = (dateStr) => {
  if (!dateStr) return false;
  return dateStr.startsWith(getISTDate());
};

/** Normalize phone number for cross-matching IVR <-> FD data */
const normalizePhone = (phone) => {
  if (!phone) return '';
  let p = String(phone).trim().replace(/[+\s-]/g, '');
  if (p.startsWith('91') && p.length > 10) p = p.slice(2);
  if (p.startsWith('0') && p.length > 10) p = p.slice(1);
  return p.length >= 10 ? p.slice(-10) : p;
};

/** Format seconds into Xm Ys */
const formatDuration = (seconds) => {
  const s = safeNum(seconds);
  if (s <= 0) return '0s';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

/** Format a date as "DD MMM" e.g. "10 Feb" */
const formatDOJ = (dateStr) => {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '--';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]}`;
  } catch { return '--'; }
};

/** Calculate days since a date */
const daysSince = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    return Math.floor((now - d) / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

/** Map source_name to display label and color */
const SOURCE_MAP = {
  wtf_one_day_plan: { label: '1-Day Trial', color: 'bg-blue-900/60 text-blue-300 border-blue-700' },
  wati: { label: 'WhatsApp', color: 'bg-green-900/60 text-green-300 border-green-700' },
  gym_visit: { label: 'Walk-in', color: 'bg-purple-900/60 text-purple-300 border-purple-700' },
  referral: { label: 'Referral', color: 'bg-amber-900/60 text-amber-300 border-amber-700' },
  renewal: { label: 'Renewal', color: 'bg-teal-900/60 text-teal-300 border-teal-700' },
  company_leads: { label: 'Corporate', color: 'bg-indigo-900/60 text-indigo-300 border-indigo-700' },
  cold_calling: { label: 'Cold Call', color: 'bg-slate-700/60 text-slate-300 border-slate-600' },
  enquiry: { label: 'Enquiry', color: 'bg-cyan-900/60 text-cyan-300 border-cyan-700' },
  ivr: { label: 'IVR', color: 'bg-orange-900/60 text-orange-300 border-orange-700' },
  walkin: { label: 'Walk-in', color: 'bg-purple-900/60 text-purple-300 border-purple-700' },
  trial: { label: 'Trial', color: 'bg-sky-900/60 text-sky-300 border-sky-700' },
  unpaid: { label: 'Unpaid', color: 'bg-red-900/60 text-red-300 border-red-700' },
};

const SourceBadge = ({ source }) => {
  if (!source) return <span className="text-slate-600 text-[10px]">--</span>;
  const mapped = SOURCE_MAP[source.toLowerCase()];
  const label = mapped ? mapped.label : source.charAt(0).toUpperCase() + source.slice(1);
  const colorClass = mapped ? mapped.color : 'bg-slate-700/60 text-slate-300 border-slate-600';
  return (
    <span className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${colorClass}`}>
      {label}
    </span>
  );
};

// ─── SORT ICON ────────────────────────────────────────────────────────────────

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <ChevronUp className="w-3 h-3 text-slate-600 opacity-40" />;
  }
  return sortConfig.direction === 'asc'
    ? <ChevronUp className="w-3 h-3 text-amber-400" />
    : <ChevronDown className="w-3 h-3 text-amber-400" />;
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon: Icon, color = 'text-white', valueColor, pulse, onClick, active }) => (
  <div
    className={`flex items-center gap-3 bg-slate-800/60 border rounded-lg px-4 py-3 min-w-[160px] cursor-pointer transition-all duration-150 hover:scale-[1.02] hover:border-slate-500 ${active ? 'border-amber-500/70 ring-1 ring-amber-500/30' : 'border-slate-700/50'}`}
    onClick={onClick}
  >
    {Icon && (
      <div className={`p-2 rounded-md bg-slate-900/80 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium truncate">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${valueColor || 'text-white'} ${pulse ? 'animate-pulse' : ''}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function SalesIntelligence() {
  const navigate = useNavigate();
  // ── User role info (cosmetic scope indicator) ──
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = storedUser.role || 'admin';
  const userName = storedUser.name || '';

  // ── WTF Feeds ──
  const { data: settlementData, loading: settlLoading, refresh: refreshSettlement, lastSync: settlSync } = useWtfLiveData('settlement_stats');
  const { data: fdData, loading: fdLoading, refresh: refreshFd } = useWtfLiveData('fd_performance');
  const { data: leadStats, loading: leadLoading, refresh: refreshLeads } = useWtfLiveData('lead_stats_v2');
  const { data: walkinRaw } = useWtfLiveData('walkin_stats');
  const { data: subscriptionStats, loading: subStatsLoading } = useWtfLiveData('subscription_type_stats');
  const { data: dashboardReports } = useWtfLiveData('dashboard_reports');
  const { data: leadStatsPerGym } = useWtfLiveData('lead_stats_per_gym');

  // ── Walkin phone-to-lead lookup (for enriching call recordings) ──
  const walkinByPhone = useMemo(() => {
    const map = {};
    const walkins = walkinRaw?.data || (Array.isArray(walkinRaw) ? walkinRaw : []);
    walkins.forEach(w => {
      const phone = normalizePhone(w.mobile);
      if (phone) {
        map[phone] = {
          name: w.name || '',
          source: w.source_name || '',
          submissionType: w.submission_type || '',
          gymName: w.gym_name || '',
          leadId: w.lead_id || '',
          status: w.status || '',
        };
      }
    });
    return map;
  }, [walkinRaw]);

  // ── Derived: Per-gym daily sales from subscription_type_stats ──
  const gymSalesToday = useMemo(() => {
    const lookup = {};
    const stats = Array.isArray(subscriptionStats) ? subscriptionStats : [];
    stats.forEach(g => {
      const key = normalizeGymName(g.gym_name);
      if (key) {
        lookup[key] = {
          total: safeNum(g.total),
          regular: safeNum(g.regular),
          renewals: safeNum(g.renewals),
          newSubs: safeNum(g.new_subscriptions),
          recurring: safeNum(g.recurring),
          addonPt: safeNum(g.addon_pt),
          regularAchieved: safeNum(g.regular_acheived),
          addonPtAchieved: safeNum(g.addon_pt_acheived),
          activeMembers: safeNum(g.active_members),
          totalCheckins: safeNum(g.total_checkins),
        };
      }
    });
    return lookup;
  }, [subscriptionStats]);

  // ── Derived: Dashboard reports (global today's sales summary) ──
  const dashReports = useMemo(() => {
    if (!dashboardReports || Array.isArray(dashboardReports)) return {};
    return dashboardReports;
  }, [dashboardReports]);

  // ── Derived: Per-gym followup stats from lead_stats_per_gym ──
  const gymFollowups = useMemo(() => {
    const lookup = {};
    const stats = Array.isArray(leadStatsPerGym) ? leadStatsPerGym : [];
    stats.forEach(g => {
      if (g.gym_id) {
        lookup[g.gym_id] = {
          todaysFollowups: safeNum(g.todays_follow_ups),
          followupsAdded: safeNum(g.followups_added_today),
          hotLeads: safeNum(g.hot_leads),
          semiHotLeads: safeNum(g.semi_hot_leads),
          warmLeads: safeNum(g.warm_leads),
          coldLeads: safeNum(g.cold_leads),
          missedFollowups: safeNum(g.missed_followups),
          todaysLeads: safeNum(g.todays_leads),
          allLeads: safeNum(g.all_leads),
          convertedLeads: safeNum(g.converted_leads),
        };
      }
    });
    return lookup;
  }, [leadStatsPerGym]);

  // ── IVR Data ──
  const [ivrData, setIvrData] = useState({ callLogs: [], c2cLogs: [] });
  const [ivrLoading, setIvrLoading] = useState(true);

  useEffect(() => {
    const fetchIvr = async () => {
      try {
        const [callRes, c2cRes] = await Promise.all([
          axiosInstance.get('/ivr/call_logs'),
          axiosInstance.get('/ivr/c2c_logs'),
        ]);
        setIvrData({
          callLogs: callRes.data?.data || [],
          c2cLogs: c2cRes.data?.data || [],
        });
      } catch (e) {
        console.error('IVR fetch failed:', e);
      } finally {
        setIvrLoading(false);
      }
    };
    fetchIvr();
    const timer = setInterval(fetchIvr, 300000);
    return () => clearInterval(timer);
  }, []);

  // ── Live Calls removed: IVR API get_call_status returns disposition categories, not live calls ──

  // ── Staff Directory (DOJ data) ──
  const [staffDirectory, setStaffDirectory] = useState([]);

  useEffect(() => {
    axiosInstance.get('/wtf-data/staff-directory')
      .then(r => setStaffDirectory(r.data?.data || []))
      .catch(() => {});
  }, []);

  // ── FD Call Minutes (150-min target tracking) ──
  const [fdCallMinutes, setFdCallMinutes] = useState({});

  useEffect(() => {
    const fetchCallMinutes = () => {
      axiosInstance.get('/wtf-data/fd-call-minutes')
        .then(r => setFdCallMinutes(r.data?.data || {}))
        .catch(() => {});
    };
    fetchCallMinutes();
    const interval = setInterval(fetchCallMinutes, 300000);
    return () => clearInterval(interval);
  }, []);

  // ── Closure Expected Data (today + tomorrow) ──
  const [closureToday, setClosureToday] = useState([]);
  const [closureTomorrow, setClosureTomorrow] = useState([]);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureDialogGym, setClosureDialogGym] = useState(null);
  const [closureDialogDate, setClosureDialogDate] = useState(null);

  useEffect(() => {
    const fetchClosure = async () => {
      try {
        const today = getISTDate();
        // Add 24h to the IST-shifted epoch (same trick as getISTDate but +1 day)
        const nowMs = new Date().getTime() + (5.5 * 60 * 60 * 1000) + 86400000;
        const tomorrow = new Date(nowMs).toISOString().split('T')[0];

        const [todayRes, tmrwRes] = await Promise.all([
          axiosInstance.get('/closure-expected', { params: { date: today } }).catch(() => ({ data: { data: [] } })),
          axiosInstance.get('/closure-expected', { params: { date: tomorrow } }).catch(() => ({ data: { data: [] } })),
        ]);
        setClosureToday(todayRes.data?.data || []);
        setClosureTomorrow(tmrwRes.data?.data || []);
      } catch (e) {
        console.error('Closure expected fetch failed:', e);
      }
    };
    fetchClosure();
    const timer = setInterval(fetchClosure, 300000); // refresh every 5 min
    return () => clearInterval(timer);
  }, []);

  // ── Derived: Closure lookup maps by normalized gym name ──
  const closureTodayByGym = useMemo(() => {
    const lookup = {};
    closureToday.forEach(g => {
      const key = normalizeGymName(g.gym_name);
      if (key) lookup[key] = { count: g.total_closure_expected || 0, gym_id: g.gym_id };
    });
    return lookup;
  }, [closureToday]);

  const closureTmrwByGym = useMemo(() => {
    const lookup = {};
    closureTomorrow.forEach(g => {
      const key = normalizeGymName(g.gym_name);
      if (key) lookup[key] = { count: g.total_closure_expected || 0, gym_id: g.gym_id };
    });
    return lookup;
  }, [closureTomorrow]);

  // ── FD Today Stats (per-gym, fetched on drill-down) ──
  const [fdTodayStats, setFdTodayStats] = useState([]);

  // ── Active KPI panel ──
  const [activeKpi, setActiveKpi] = useState(null);

  // ── View State ──
  const [selectedGym, setSelectedGym] = useState(null);
  const [selectedGymId, setSelectedGymId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [gymSortConfig, setGymSortConfig] = useState({ key: 'achievement_pct', direction: 'desc' });
  const [fdSortConfig, setFdSortConfig] = useState({ key: 'achievement_pct', direction: 'desc' });

  // ── TL/Manager Filter State (lifted from table to parent for KPI filtering) ──
  const [tlFilter, setTlFilter] = useState('');
  const [mgrFilter, setMgrFilter] = useState('');

  // ── Derived: Settlement array (safe) ──
  const settlements = useMemo(() => {
    if (!settlementData) return [];
    return Array.isArray(settlementData) ? settlementData : [];
  }, [settlementData]);

  // ── Derived: FD array (safe) ──
  const fds = useMemo(() => {
    if (!fdData) return [];
    return Array.isArray(fdData) ? fdData : [];
  }, [fdData]);

  // ── Derived: Lead stats (single object) ──
  // Preserve last known good aggregate data so KPI cards stay accurate even when
  // the backend cache temporarily returns empty (circuit breaker / sync gap).
  const leadDataRef = useRef({});
  const leadData = useMemo(() => {
    if (leadStats && !Array.isArray(leadStats) && Object.keys(leadStats).length > 0) {
      leadDataRef.current = leadStats;
      return leadStats;
    }
    return leadDataRef.current;
  }, [leadStats]);

  // ── Derived: FD count by normalized gym name ──
  const fdCountByGym = useMemo(() => {
    const lookup = {};
    fds.forEach((fd) => {
      const key = normalizeGymName(fd.gym_name);
      if (key) {
        lookup[key] = (lookup[key] || 0) + 1;
      }
    });
    return lookup;
  }, [fds]);

  // ── Derived: Gym-level call stats from fdCallMinutes (150-min target tracking) ──
  const gymCallStats = useMemo(() => {
    const stats = {};
    const fdList = fds || [];

    // Map each FD to their gym, look up their call minutes
    fdList.forEach(fd => {
      const phone = normalizePhone(fd.mobile);
      const gymKey = normalizeGymName(fd.gym_name || '');
      if (!gymKey) return;

      if (!stats[gymKey]) {
        stats[gymKey] = { total_sec: 0, total_calls: 0, connected_calls: 0, calls_with_duration: 0, unique_customers: 0, unique_connected: 0, fd_count: 0, c2c_calls: 0, c2c_connected: 0, inc_calls: 0, inc_connected: 0 };
      }
      stats[gymKey].fd_count++;

      const callData = fdCallMinutes[phone] || (() => {
        // Try matching with normalized keys
        for (const [key, val] of Object.entries(fdCallMinutes)) {
          if (normalizePhone(key) === phone) return val;
        }
        return null;
      })();

      if (callData) {
        stats[gymKey].total_sec += (safeNum(callData.c2c_minutes) + safeNum(callData.incoming_minutes)) * 60;
        stats[gymKey].total_calls += safeNum(callData.total_calls);
        stats[gymKey].connected_calls += safeNum(callData.connected_calls);
        stats[gymKey].calls_with_duration += safeNum(callData.calls_with_duration);
        stats[gymKey].unique_customers += safeNum(callData.unique_customers);
        stats[gymKey].unique_connected += safeNum(callData.unique_connected);
        stats[gymKey].c2c_calls += safeNum(callData.c2c_calls);
        stats[gymKey].c2c_connected += safeNum(callData.c2c_connected);
        stats[gymKey].inc_calls += safeNum(callData.incoming_calls);
        stats[gymKey].inc_connected += safeNum(callData.incoming_connected);
      }
    });

    // Compute derived values
    Object.keys(stats).forEach(gym => {
      const s = stats[gym];
      s.total_minutes = Math.round(s.total_sec / 60 * 10) / 10;
      s.target_minutes = s.fd_count * 150;
      s.call_target_pct = s.target_minutes > 0 ? Math.round((s.total_minutes / s.target_minutes) * 100) : 0;
      s.avg_per_call_sec = s.calls_with_duration > 0 ? Math.round(s.total_sec / s.calls_with_duration) : 0;
      s.avg_per_unique_sec = s.unique_connected > 0 ? Math.round(s.total_sec / s.unique_connected) : 0;
      s.connect_pct = s.c2c_calls > 0 ? Math.round((s.c2c_connected / s.c2c_calls) * 100) : 0;
    });

    return stats;
  }, [fdCallMinutes, fds]);

  // ── Derived: Org-wide call totals from gymCallStats (same source as table) ──
  const orgCallStats = useMemo(() => {
    let totalCalls = 0, outbound = 0, c2cConnected = 0, incCalls = 0, incConnected = 0;
    Object.values(gymCallStats).forEach(s => {
      totalCalls += s.total_calls;
      outbound += s.c2c_calls;
      c2cConnected += s.c2c_connected;
      incCalls += s.inc_calls;
      incConnected += s.inc_connected;
    });
    const inbound = incCalls;
    const missedInbound = Math.max(0, incCalls - incConnected);
    const c2cPct = outbound > 0 ? Math.round((c2cConnected / outbound) * 100) : 0;
    return { totalCalls, inbound, outbound, c2cConnected, missedInbound, c2cPct };
  }, [gymCallStats]);

  // ── Derived: Today's IVR call count ──
  const todayCallCount = useMemo(() => {
    const today = getISTDate();
    return ivrData.callLogs.filter((log) => {
      const callTime = log.call_time || log.created_at || log.date || '';
      return callTime.startsWith(today);
    }).length;
  }, [ivrData.callLogs]);

  // ── Derived: Today's C2C counts (pre-computed for KPI) ──
  const todayC2cCount = useMemo(() => {
    return ivrData.c2cLogs.filter(l => isToday(l.call_time || l.created_at)).length;
  }, [ivrData.c2cLogs]);

  const todayC2cConnected = useMemo(() => {
    return ivrData.c2cLogs.filter(l => isToday(l.call_time || l.created_at) && l.disposition === 'answered').length;
  }, [ivrData.c2cLogs]);

  // ── Derived: C2C Connect Rate ──
  const c2cConnectRate = useMemo(() => {
    if (todayC2cCount === 0) return { pct: 0, connected: 0, total: 0 };
    const pct = Math.round((todayC2cConnected / todayC2cCount) * 100);
    return { pct, connected: todayC2cConnected, total: todayC2cCount };
  }, [todayC2cCount, todayC2cConnected]);

  // ── Derived: Missed inbound calls (customer called, nobody picked up) ──
  const missedInboundCount = useMemo(() => {
    const today = getISTDate();
    return ivrData.callLogs.filter(log => {
      const callTime = log.call_time || log.created_at || log.date || '';
      if (!callTime.startsWith(today)) return false;
      return safeNum(log.call_duration) === 0;
    }).length;
  }, [ivrData.callLogs]);

  // ── Derived: C2C disposition breakdown ──
  const c2cDispositions = useMemo(() => {
    const breakdown = {};
    ivrData.c2cLogs.forEach(l => {
      if (!isToday(l.call_time || l.created_at)) return;
      const d = l.disposition || 'unknown';
      breakdown[d] = (breakdown[d] || 0) + 1;
    });
    return breakdown;
  }, [ivrData.c2cLogs]);

  // ── Derived: IVR per-FD lookup (normalized mobile -> stats) ──
  const fdIvrLookup = useMemo(() => {
    const lookup = {};
    const todayDate = getISTDate(); // YYYY-MM-DD in IST, matches backend timezone
    // Build FD mobile set for quick lookup
    const fdMobileToName = {};
    fds.forEach(fd => {
      const m = normalizePhone(fd.mobile);
      if (m) fdMobileToName[m] = fd.name || fd.fd_name || fd.staff_name || 'Unknown';
    });

    const initStats = () => ({
      totalCalls: 0, uniqueClients: new Set(), connectedDuration: 0, callsWithDuration: 0,
      recordings: [], c2cCalls: 0, connectedC2c: 0, incCalls: 0, incConnected: 0,
      uniqueConnected: new Set()
    });

    // Process call_logs: attended_by = FD who picked up
    ivrData.callLogs.forEach(log => {
      const fdPhone = normalizePhone(log.attended_by);
      if (!fdPhone || !fdMobileToName[fdPhone]) return;
      if (!lookup[fdPhone]) lookup[fdPhone] = initStats();
      const stats = lookup[fdPhone];
      const dur = safeNum(log.call_duration);
      const callTime = log.call_time || '';
      const isTodayCall = callTime.startsWith(todayDate);
      const clientPhone = normalizePhone(log.client_no);
      const lead = clientPhone ? walkinByPhone[clientPhone] : null;

      // Only count today's calls in metrics (but keep all recordings)
      if (isTodayCall) {
        stats.incCalls++;
        stats.totalCalls++;
        if (dur > 0) {
          stats.connectedDuration += dur;
          stats.callsWithDuration++;
          stats.incConnected++;
          if (clientPhone) stats.uniqueConnected.add(clientPhone);
        }
        if (clientPhone) stats.uniqueClients.add(clientPhone);
      }
      stats.recordings.push({
        url: log.recording_url || '',
        time: log.call_time,
        duration: dur,
        clientNo: log.client_no || '',
        type: 'incoming',
        direction: 'Inbound',
        disposition: dur > 0 ? 'connected' : 'missed',
        didNo: log.did_no || '',
        leadSource: lead?.source || '',
        leadName: lead?.name || '',
        leadStatus: lead?.status || '',
        isToday: isTodayCall,
      });
    });

    // Process c2c_logs: agent_no = FD who made the call
    ivrData.c2cLogs.forEach(log => {
      const fdPhone = normalizePhone(log.agent_no);
      if (!fdPhone || !fdMobileToName[fdPhone]) return;
      if (!lookup[fdPhone]) lookup[fdPhone] = initStats();
      const stats = lookup[fdPhone];
      const dur = safeNum(log.call_duration);
      const callTime = log.call_time || '';
      const isTodayCall = callTime.startsWith(todayDate);
      const isAnswered = log.disposition === 'answered';
      const clientPhone = normalizePhone(log.client_no);
      const lead = clientPhone ? walkinByPhone[clientPhone] : null;

      // Only count today's calls in metrics
      if (isTodayCall) {
        stats.c2cCalls++;
        stats.totalCalls++;
        if (isAnswered) {
          stats.connectedC2c++;
          if (clientPhone) stats.uniqueConnected.add(clientPhone);
          if (dur > 0) {
            stats.connectedDuration += dur;
            stats.callsWithDuration++;
          }
        }
        if (clientPhone) stats.uniqueClients.add(clientPhone);
      }
      stats.recordings.push({
        url: log.recording_url || '',
        time: log.call_time,
        duration: dur,
        clientNo: log.client_no || '',
        type: 'c2c',
        direction: 'Outbound',
        disposition: isAnswered ? 'connected' : 'missed',
        customerStatus: log.customer_call_status || '',
        didNo: log.did_no || '',
        leadSource: lead?.source || '',
        leadName: lead?.name || '',
        leadStatus: lead?.status || '',
        isToday: isTodayCall,
      });
    });

    // Convert Sets to counts — avg uses only connected calls with duration (matches backend logic)
    const result = {};
    Object.entries(lookup).forEach(([phone, stats]) => {
      const uniqueCount = stats.uniqueClients.size;
      const uniqueConnectedCount = stats.uniqueConnected.size;
      result[phone] = {
        ...stats,
        uniqueClients: uniqueCount,
        uniqueConnected: uniqueConnectedCount,
        avgDuration: stats.callsWithDuration > 0 ? Math.round(stats.connectedDuration / stats.callsWithDuration) : 0,
        avgDurationPerUnique: uniqueConnectedCount > 0 ? Math.round(stats.connectedDuration / uniqueConnectedCount) : 0,
      };
    });
    return result;
  }, [ivrData, fds, walkinByPhone]);

  // ── Gym Hierarchy (TL/Manager for ALL gyms - used in main table + FD drill-down) ──
  const [gymHierarchy, setGymHierarchy] = useState({});
  const [gymHierarchyById, setGymHierarchyById] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await axiosInstance.get('/fd-hierarchy/all');
        if (res.data?.data) setGymHierarchy(res.data.data);
        if (res.data?.by_gym_id) setGymHierarchyById(res.data.by_gym_id);
      } catch (e) {
        console.warn('Failed to fetch gym hierarchy:', e);
      }
    };
    fetchAll();
  }, []);

  // ── Derived: KPI aggregates from settlement_stats ──
  const kpiAggregates = useMemo(() => {
    let monthlyTarget = 0;
    let monthlyAchieved = 0;
    settlements.forEach((gym) => {
      monthlyTarget += safeNum(gym.target_value);
      monthlyAchieved += safeNum(gym.target_acheived);
    });
    const achievementPercent = achievementPct(monthlyAchieved, monthlyTarget);
    return { monthlyTarget, monthlyAchieved, achievementPercent };
  }, [settlements]);

  // ── Derived: Gym table rows (enriched with FD count, IVR stats, computed fields) ──
  const gymRows = useMemo(() => {
    return settlements.map((gym) => {
      const target = safeNum(gym.target_value);
      const achieved = safeNum(gym.target_acheived);
      const remaining = Math.max(target - achieved, 0);
      const pct = achievementPct(achieved, target);
      const todayTarget = safeNum(gym.today_target || gym.todays_target);
      const todayAchieved = safeNum(gym.today_achieved || gym.todays_achieved);
      const deficit = Math.max(todayTarget - todayAchieved, 0);
      const normalizedName = normalizeGymName(gym.gym_name);
      const fdCount = fdCountByGym[normalizedName] || 0;

      // IVR stats for this gym: sum up all FDs' IVR data
      let gymCalls = 0, gymUnique = 0, gymDuration = 0;
      const gymFds = fds.filter(fd => normalizeGymName(fd.gym_name) === normalizedName);
      gymFds.forEach(fd => {
        const m = normalizePhone(fd.mobile);
        const ivrStats = fdIvrLookup[m];
        if (ivrStats) {
          gymCalls += ivrStats.totalCalls;
          gymUnique += ivrStats.uniqueClients;
          gymDuration += ivrStats.connectedDuration;
        }
      });
      const callTarget = fdCount * 15; // 150 min / 10 min per unique customer

      // TL/Manager from bulk hierarchy lookup (prefer gym_id match, fall back to name)
      const hier = gymHierarchyById[gym.gym_id] || gymHierarchy[normalizedName];
      const tlName = hier?.tl_name || '--';
      const mgrName = hier?.manager_name || '--';

      // Today's sales from subscription_type_stats
      const gymSales = gymSalesToday[normalizedName];
      const salesToday = gymSales ? gymSales.total : null;

      // Per-gym followup stats from lead_stats_per_gym
      const gymFollow = gym.gym_id ? gymFollowups[gym.gym_id] : null;

      // Closure expected counts
      const clsToday = closureTodayByGym[normalizedName];
      const clsTmrw = closureTmrwByGym[normalizedName];

      return {
        gym_name: gym.gym_name || 'Unknown',
        normalized_name: normalizedName,
        active_members: gymSales ? gymSales.activeMembers : null,
        target,
        achieved,
        remaining,
        achievement_pct: pct,
        today_target: todayTarget,
        today_achieved: todayAchieved,
        deficit,
        fd_count: fdCount,
        rev_per_fd: fdCount > 0 ? Math.round(achieved / fdCount) : 0,
        sales_today: salesToday,
        closure_today: clsToday ? clsToday.count : null,
        closure_today_gym_id: clsToday?.gym_id || gym.gym_id || '',
        closure_tomorrow: clsTmrw ? clsTmrw.count : null,
        closure_tomorrow_gym_id: clsTmrw?.gym_id || gym.gym_id || '',
        followups_today: gymFollow ? gymFollow.followupsAdded : null,
        followups_interested: gymFollow ? (gymFollow.hotLeads + gymFollow.semiHotLeads) : null,
        ivr_calls: gymCalls,
        ivr_unique: gymUnique,
        ivr_duration: gymDuration,
        ivr_target: callTarget,
        tl_name: tlName,
        manager_name: mgrName,
        raw: gym,
      };
    });
  }, [settlements, fdCountByGym, fds, fdIvrLookup, gymHierarchy, gymHierarchyById, gymSalesToday, gymFollowups, closureTodayByGym, closureTmrwByGym]);

  // ── Derived: Contextual KPIs for selected gym ──
  const selectedGymKpi = useMemo(() => {
    if (!selectedGym) return null;
    const gymRow = gymRows.find(r => r.gym_name === selectedGym);
    if (!gymRow) return null;
    const followup = selectedGymId ? gymFollowups[selectedGymId] : null;
    const sales = gymSalesToday[normalizeGymName(selectedGym)];
    const callStats = gymCallStats[gymRow.normalized_name];
    return {
      gymName: selectedGym,
      monthlyTarget: gymRow.target,
      monthlyAchieved: gymRow.achieved,
      achievementPercent: gymRow.achievement_pct,
      salesToday: sales?.total || 0,
      salesMtd: gymRow.achieved,
      followupsToday: followup?.todaysFollowups ?? '--',
      followupsAdded: followup?.followupsAdded ?? '--',
      todaysLeads: followup?.todaysLeads ?? '--',
      hotLeads: followup?.hotLeads ?? '--',
      missedFollowups: followup?.missedFollowups ?? '--',
      convertedLeads: followup?.convertedLeads ?? '--',
      allLeads: followup?.allLeads ?? 1,
      totalCalls: callStats?.total_calls || gymRow.ivr_calls || 0,
      totalMinutes: callStats?.total_minutes || 0,
      uniqueCustomers: callStats?.unique_customers || gymRow.ivr_unique || 0,
      fdCount: gymRow.fd_count,
      c2cRate: callStats && callStats.c2c_calls > 0 ? Math.round((callStats.c2c_connected / callStats.c2c_calls) * 100) : 0,
      c2cConnected: callStats?.c2c_connected || 0,
      c2cTotal: callStats?.c2c_calls || 0,
      missedCalls: callStats ? Math.max(0, (callStats.inc_calls || 0) - (callStats.inc_connected || 0)) : 0,
    };
  }, [selectedGym, selectedGymId, gymRows, gymFollowups, gymSalesToday, gymCallStats]);

  // ── Derived: Filtered + sorted gym rows ──
  const filteredGymRows = useMemo(() => {
    let rows = gymRows;
    // Apply TL filter
    if (tlFilter) {
      rows = rows.filter((r) => r.tl_name === tlFilter);
    }
    // Apply Manager filter
    if (mgrFilter) {
      rows = rows.filter((r) => r.manager_name === mgrFilter);
    }
    // Apply search term filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((r) => r.gym_name.toLowerCase().includes(q));
    }
    // Apply sorting
    if (gymSortConfig.key) {
      rows = [...rows].sort((a, b) => {
        let aVal, bVal;
        // Special handling for call_min which comes from gymCallStats
        if (gymSortConfig.key === 'call_min') {
          aVal = gymCallStats[a.normalized_name]?.total_minutes || 0;
          bVal = gymCallStats[b.normalized_name]?.total_minutes || 0;
        } else {
          aVal = a[gymSortConfig.key];
          bVal = b[gymSortConfig.key];
        }
        // Treat null as -1 so they sort to the bottom
        if (aVal === null || aVal === undefined) aVal = -1;
        if (bVal === null || bVal === undefined) bVal = -1;
        if (typeof aVal === 'string') {
          return gymSortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        return gymSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    return rows;
  }, [gymRows, searchTerm, gymSortConfig, tlFilter, mgrFilter, gymCallStats]);

  // ── Derived: Extract unique TL and Manager names for filter dropdowns ──
  const uniqueTLs = useMemo(() => {
    const names = new Set();
    gymRows.forEach(r => { if (r.tl_name && r.tl_name !== '--') names.add(r.tl_name); });
    return Array.from(names).sort();
  }, [gymRows]);

  const uniqueManagers = useMemo(() => {
    const names = new Set();
    gymRows.forEach(r => { if (r.manager_name && r.manager_name !== '--') names.add(r.manager_name); });
    return Array.from(names).sort();
  }, [gymRows]);

  // ── Derived: Filtered call totals from gymCallStats (for TL/Manager filter) ──
  const filteredCallStats = useMemo(() => {
    if (!tlFilter && !mgrFilter) return null;
    let totalCalls = 0, outbound = 0, c2cConnected = 0, incCalls = 0, incConnected = 0;
    filteredGymRows.forEach(row => {
      const cs = gymCallStats[row.normalized_name];
      if (!cs) return;
      totalCalls += cs.total_calls;
      outbound += cs.c2c_calls;
      c2cConnected += cs.c2c_connected;
      incCalls += cs.inc_calls;
      incConnected += cs.inc_connected;
    });
    const inbound = incCalls;
    const missedInbound = Math.max(0, incCalls - incConnected);
    const c2cPct = outbound > 0 ? Math.round((c2cConnected / outbound) * 100) : 0;
    return { totalCalls, inbound, outbound, c2cConnected, missedInbound, c2cPct };
  }, [tlFilter, mgrFilter, filteredGymRows, gymCallStats]);

  // ── Derived: FD phones for filtered gyms (used for IVR filtering) ──
  const filteredFdPhones = useMemo(() => {
    if (!tlFilter && !mgrFilter) return null; // null = no filter active
    const phones = new Set();
    const filteredGymNames = new Set(filteredGymRows.map(r => r.normalized_name));
    (fds || []).forEach(fd => {
      const gymKey = normalizeGymName(fd.gym_name || '');
      if (filteredGymNames.has(gymKey)) {
        const p = normalizePhone(fd.mobile);
        if (p) phones.add(p);
      }
    });
    return phones;
  }, [tlFilter, mgrFilter, filteredGymRows, fds]);

  // ── Derived: Filtered IVR metrics (inbound/outbound/missed/live) ──
  const filteredIvrMetrics = useMemo(() => {
    if (!filteredFdPhones) return null; // no filter active
    const today = getISTDate();
    let inboundCount = 0, outboundCount = 0, outboundConnected = 0, missedInbound = 0;

    ivrData.callLogs.forEach(log => {
      const callTime = log.call_time || log.created_at || log.date || '';
      if (!callTime.startsWith(today)) return;
      const fdPhone = normalizePhone(log.attended_by);
      if (!fdPhone || !filteredFdPhones.has(fdPhone)) return;
      inboundCount++;
      if (safeNum(log.call_duration) === 0) missedInbound++;
    });

    ivrData.c2cLogs.forEach(log => {
      if (!isToday(log.call_time || log.created_at)) return;
      const fdPhone = normalizePhone(log.agent_no);
      if (!fdPhone || !filteredFdPhones.has(fdPhone)) return;
      outboundCount++;
      if (log.disposition === 'answered') outboundConnected++;
    });

    const c2cPct = outboundCount > 0 ? Math.round((outboundConnected / outboundCount) * 100) : 0;

    return { inboundCount, outboundCount, outboundConnected, missedInbound, c2cPct };
  }, [filteredFdPhones, ivrData]);

  // ── Derived: Filtered KPI aggregates (respects TL/Manager filter) ──
  const filteredKpiAggregates = useMemo(() => {
    // Compute from filtered gym rows (or all rows if no filter)
    let monthlyTarget = 0;
    let monthlyAchieved = 0;
    let salesToday = 0;
    let followupsToday = 0;
    let todaysLeads = 0;
    let missedFollowups = 0;
    let hotLeads = 0;
    let convertedLeads = 0;
    let allLeads = 0;
    let totalCalls = 0;

    filteredGymRows.forEach((gym) => {
      monthlyTarget += safeNum(gym.target);
      monthlyAchieved += safeNum(gym.achieved);
      salesToday += safeNum(gym.sales_today);
      followupsToday += safeNum(gym.followups_today);
      todaysLeads += safeNum(gymFollowups[gym.raw?.gym_id]?.todaysLeads || 0);
      missedFollowups += safeNum(gymFollowups[gym.raw?.gym_id]?.missedFollowups || 0);
      hotLeads += safeNum(gymFollowups[gym.raw?.gym_id]?.hotLeads || 0);
      convertedLeads += safeNum(gymFollowups[gym.raw?.gym_id]?.convertedLeads || 0);
      allLeads += safeNum(gymFollowups[gym.raw?.gym_id]?.allLeads || 0);
      totalCalls += safeNum(gym.ivr_calls);
    });

    // When no TL/Manager filter is active AND user is admin, use aggregate lead_stats_v2
    // values for lead metrics — the per-gym sum undercounts because lead_stats_per_gym only
    // covers gyms with settlement data, missing leads from unsettled gyms.
    // Non-admin users (TL/manager) already have role-scoped gym rows, so use per-gym sums.
    const isUnfiltered = !tlFilter && !mgrFilter && userRole === 'admin';
    if (isUnfiltered && leadData && Object.keys(leadData).length > 0) {
      followupsToday = safeNum(leadData.followups_added_today);
      todaysLeads = safeNum(leadData.todays_leads);
      missedFollowups = safeNum(leadData.missed_followups);
      hotLeads = safeNum(leadData.hot_leads);
      convertedLeads = safeNum(leadData.converted_leads);
      allLeads = safeNum(leadData.all_leads);
    }

    const achievementPercent = achievementPct(monthlyAchieved, monthlyTarget);
    return {
      monthlyTarget,
      monthlyAchieved,
      achievementPercent,
      salesToday,
      followupsToday,
      todaysLeads,
      missedFollowups,
      hotLeads,
      convertedLeads,
      allLeads,
      totalCalls,
    };
  }, [filteredGymRows, gymFollowups, tlFilter, mgrFilter, leadData]);

  // ── Derived: Walk-in count (respects filters + gym selection) ──
  const computedWalkinCount = useMemo(() => {
    const walkins = walkinRaw?.data || (Array.isArray(walkinRaw) ? walkinRaw : []);
    if (selectedGym) {
      const normSelected = normalizeGymName(selectedGym);
      return walkins.filter(w => normalizeGymName(w.gym_name || '') === normSelected).length;
    }
    if (filteredFdPhones) {
      const filteredGymNames = new Set(filteredGymRows.map(r => r.normalized_name));
      return walkins.filter(w => filteredGymNames.has(normalizeGymName(w.gym_name || ''))).length;
    }
    return walkins.length;
  }, [walkinRaw, selectedGym, filteredFdPhones, filteredGymRows]);

  // ── Derived: FD rows for selected gym ──
  const selectedGymFds = useMemo(() => {
    if (!selectedGym) return [];
    const normalizedSelected = normalizeGymName(selectedGym);
    return fds
      .filter((fd) => normalizeGymName(fd.gym_name) === normalizedSelected)
      .map((fd) => {
        const target = safeNum(fd.target_value || fd.target);
        const achieved = safeNum(fd.target_acheived || fd.achieved);
        const remaining = Math.max(target - achieved, 0);
        const pct = achievementPct(achieved, target);
        const todayTarget = safeNum(fd.today_target || fd.todays_target);
        const todayAchieved = safeNum(fd.today_achieved || fd.todays_achieved);
        const deficit = Math.max(todayTarget - todayAchieved, 0);

        const m = normalizePhone(fd.mobile);
        const ivrStats = fdIvrLookup[m] || { totalCalls: 0, uniqueClients: 0, connectedDuration: 0, callsWithDuration: 0, avgDuration: 0, avgDurationPerUnique: 0, recordings: [], c2cCalls: 0, connectedC2c: 0, incCalls: 0, incConnected: 0, uniqueConnected: 0 };

        // Gym-level TL/Manager from bulk hierarchy (all FDs in a gym share the same TL/Manager)
        const hier = gymHierarchyById[selectedGymId] || gymHierarchy[normalizedSelected];
        const tlName = hier?.tl_name || '--';
        const mgrName = hier?.manager_name || '--';

        return {
          fd_name: fd.fd_name || fd.name || fd.staff_name || 'Unknown',
          mobile: fd.mobile || fd.phone || fd.contact || '--',
          target,
          achieved,
          remaining,
          achievement_pct: pct,
          today_target: todayTarget,
          today_achieved: todayAchieved,
          deficit,
          ivr_calls: ivrStats.totalCalls,
          ivr_unique: ivrStats.uniqueClients,
          ivr_duration: ivrStats.connectedDuration,
          ivr_avg: ivrStats.avgDuration,
          ivr_avg_unique: ivrStats.avgDurationPerUnique,
          ivr_c2c: ivrStats.c2cCalls,
          ivr_connected: ivrStats.connectedC2c,
          ivr_inc: ivrStats.incCalls,
          ivr_inc_connected: ivrStats.incConnected,
          ivr_unique_connected: ivrStats.uniqueConnected,
          connectedDuration: ivrStats.connectedDuration,
          recordings: ivrStats.recordings,
          tl_name: tlName,
          manager_name: mgrName,
          raw: fd,
        };
      });
  }, [selectedGym, selectedGymId, fds, fdIvrLookup, gymHierarchy, gymHierarchyById]);

  // ── Derived: Sorted FD rows ──
  const sortedFdRows = useMemo(() => {
    if (!fdSortConfig.key) return selectedGymFds;
    return [...selectedGymFds].sort((a, b) => {
      const aVal = a[fdSortConfig.key];
      const bVal = b[fdSortConfig.key];
      if (typeof aVal === 'string') {
        return fdSortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return fdSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [selectedGymFds, fdSortConfig]);

  // ── Sort handlers ──
  const handleGymSort = useCallback((key) => {
    setGymSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const handleFdSort = useCallback((key) => {
    setFdSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  // ── Refresh all ──
  const handleRefreshAll = useCallback(async () => {
    if (userRole === 'admin') {
      try {
        // Reset circuit breaker + trigger full server-side sync first (admin only)
        await axiosInstance.post('/wtf/sync/trigger-all');
      } catch (e) { /* ignore */ }
    }
    // Then refresh from cache
    refreshSettlement();
    refreshFd();
    refreshLeads();
    toast.success('Refreshing all feeds...');
  }, [refreshSettlement, refreshFd, refreshLeads, userRole]);

  // ── KPI toggle handler ──
  const toggleKpi = useCallback((kpiId) => {
    setActiveKpi(prev => prev === kpiId ? null : kpiId);
  }, []);

  // ── Loading state ──
  const isLoading = settlLoading || fdLoading || leadLoading;

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="sales-intelligence-page">
      {/* ── AUTOMATION HEALTH (admin only) ───────────────────────────────────── */}
      {userRole === 'admin' && <AutomationHealthPanel axiosInstance={axiosInstance} />}

      {/* ── LEVEL 1: KPI HEADER BAR ─────────────────────────────────────────── */}
      <div className="bg-slate-900 border-b border-slate-700/50 px-4 py-4 sticky top-0 z-30">
        {/* Title row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-400" />
              Sales Intelligence
              {/* Pulsing live indicator */}
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <span className="text-[10px] text-green-400 font-normal uppercase tracking-widest">LIVE</span>
            </h1>
            <button onClick={() => navigate('/leaderboard')} className="ml-3 px-3 py-1.5 rounded-lg bg-yellow-600/20 border border-yellow-600/50 text-yellow-400 text-sm font-medium hover:bg-yellow-600/30 transition-colors flex items-center gap-1.5">
              <Trophy className="h-4 w-4" />
              {'\uD83C\uDFC6'} Click here for Leaderboard
            </button>
            {userRole !== 'admin' && userName && (
              <span className="ml-2 text-[11px] font-medium text-slate-400 bg-slate-800 border border-slate-700 rounded px-2 py-0.5">
                Viewing: {userName}'s gyms
              </span>
            )}
            {selectedGymKpi && (
              <span className="ml-2 text-[11px] font-medium text-cyan-300 bg-cyan-900/40 border border-cyan-700/50 rounded px-2 py-0.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                {selectedGymKpi.gymName}
                <button
                  className="ml-1 text-cyan-500 hover:text-white text-[9px]"
                  onClick={() => { setSelectedGym(null); setSelectedGymId(null); setFdTodayStats([]); }}
                >
                  ✕
                </button>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {settlSync && (
              <span className="text-[10px] text-slate-500">
                Synced {new Date(settlSync).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshAll}
              className="text-slate-400 hover:text-white h-7 px-2"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* KPI cards row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {/* 1. Followups Added Today - THE hero metric */}
          <KpiCard
            label="Followups Today"
            value={selectedGymKpi ? selectedGymKpi.followupsToday : (leadLoading ? '...' : filteredKpiAggregates.followupsToday)}
            icon={Zap}
            color="text-amber-400"
            valueColor="text-amber-400"
            pulse={!leadLoading && !selectedGymKpi}
            onClick={() => toggleKpi('followups')}
            active={activeKpi === 'followups'}
          />
          {/* 2. Today's Calls — uses gymCallStats (same source as table Calls column) */}
          {(() => {
            const cs = filteredCallStats || orgCallStats;
            return (
              <KpiCard
                label="Today's Calls"
                value={selectedGymKpi ? selectedGymKpi.totalCalls : (ivrLoading ? '...' : cs.totalCalls)}
                icon={Phone}
                color="text-blue-400"
                sub={selectedGymKpi ? `${selectedGymKpi.fdCount} FDs` : `${cs.inbound} In / ${cs.outbound} Out`}
                onClick={() => toggleKpi('calls')}
                active={activeKpi === 'calls'}
              />
            );
          })()}
          {/* 2b. Missed Calls */}
          {(() => {
            const missed = selectedGymKpi ? selectedGymKpi.missedCalls : (filteredCallStats || orgCallStats).missedInbound;
            return (
              <KpiCard
                label="Missed Calls"
                value={ivrLoading ? '...' : missed}
                icon={PhoneMissed}
                color={missed > 0 ? 'text-red-400' : 'text-slate-500'}
                onClick={() => toggleKpi('missed_calls')}
                active={activeKpi === 'missed_calls'}
              />
            );
          })()}
          {/* 3. Today's Leads */}
          <KpiCard
            label="Today's Leads"
            value={selectedGymKpi ? selectedGymKpi.todaysLeads : (leadLoading ? '...' : filteredKpiAggregates.todaysLeads)}
            icon={Users}
            color="text-cyan-400"
            onClick={() => toggleKpi('leads')}
            active={activeKpi === 'leads'}
          />
          {/* 4. Monthly Target */}
          <KpiCard
            label="Monthly Target"
            value={settlLoading ? '...' : formatINR(selectedGymKpi ? selectedGymKpi.monthlyTarget : filteredKpiAggregates.monthlyTarget)}
            icon={Target}
            color="text-slate-400"
            onClick={() => toggleKpi('monthly_target')}
            active={activeKpi === 'monthly_target'}
          />
          {/* 5. Monthly Achieved */}
          {(() => {
            const achvPct = selectedGymKpi ? selectedGymKpi.achievementPercent : filteredKpiAggregates.achievementPercent;
            const achvVal = selectedGymKpi ? selectedGymKpi.monthlyAchieved : filteredKpiAggregates.monthlyAchieved;
            return (
              <KpiCard
                label="Monthly Achieved"
                value={settlLoading ? '...' : formatINR(achvVal)}
                icon={TrendingUp}
                color={achvPct >= 50 ? 'text-green-400' : 'text-red-400'}
                valueColor={achvPct >= 50 ? 'text-green-400' : 'text-red-400'}
                onClick={() => toggleKpi('monthly_achieved')}
                active={activeKpi === 'monthly_achieved'}
              />
            );
          })()}
          {/* 6. Achievement % */}
          {(() => {
            const pct = selectedGymKpi ? selectedGymKpi.achievementPercent : filteredKpiAggregates.achievementPercent;
            return (
              <KpiCard
                label="Achievement %"
                value={settlLoading ? '...' : `${pct}%`}
                icon={Activity}
                color={pct >= 80 ? 'text-green-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}
                valueColor={pct >= 80 ? 'text-green-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}
                onClick={() => toggleKpi('achievement')}
                active={activeKpi === 'achievement'}
              />
            );
          })()}
          {/* 7. Sales Today (from dashboard reports v2) */}
          <KpiCard
            label="Sales Today"
            value={selectedGymKpi ? formatINR(selectedGymKpi.salesToday) : (subStatsLoading ? '...' : formatINR((tlFilter || mgrFilter || userRole !== 'admin') ? filteredKpiAggregates.salesToday : safeNum(dashReports.todaySale)))}
            icon={TrendingUp}
            color="text-emerald-400"
            valueColor="text-emerald-400"
            sub={selectedGymKpi ? `${selectedGymKpi.fdCount} FDs` : (tlFilter || mgrFilter || userRole !== 'admin') ? `Filtered` : (safeNum(dashReports.totalSalesMonth) > 0 ? `MTD ${formatINR(safeNum(dashReports.totalSalesMonth))}` : null)}
            onClick={() => toggleKpi('sales_today')}
            active={activeKpi === 'sales_today'}
          />
        </div>

        {/* Row 2: Secondary KPIs */}
        <div className="border-t border-slate-700/30 mt-3 pt-3">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {/* S1. Missed Followups - CRITICAL */}
            <KpiCard
              label="Missed Followups"
              value={selectedGymKpi ? selectedGymKpi.missedFollowups : (leadLoading ? '...' : filteredKpiAggregates.missedFollowups)}
              icon={AlertTriangle}
              color="text-red-400"
              valueColor={(selectedGymKpi ? safeNum(selectedGymKpi.missedFollowups) : filteredKpiAggregates.missedFollowups) > 0 ? 'text-red-400' : 'text-slate-400'}
              pulse={!selectedGymKpi && filteredKpiAggregates.missedFollowups > 0}
              onClick={() => toggleKpi('missed_followups')}
              active={activeKpi === 'missed_followups'}
            />
            {/* S1b. Missed Inbound Calls — uses gymCallStats (same source as table) */}
            {(() => {
              const cs = filteredCallStats || orgCallStats;
              const missed = cs.missedInbound;
              const inbound = cs.inbound;
              return (
                <KpiCard
                  label="Missed Inbound"
                  value={ivrLoading ? '...' : missed}
                  icon={PhoneMissed}
                  color={missed > 0 ? 'text-red-400' : 'text-green-400'}
                  valueColor={missed > 0 ? 'text-red-400' : 'text-green-400'}
                  sub={missed > 0 && inbound > 0 ? `${Math.round(missed / inbound * 100)}% unanswered` : 'all answered'}
                  pulse={missed > 5}
                  onClick={() => toggleKpi('missed_inbound')}
                  active={activeKpi === 'missed_inbound'}
                />
              );
            })()}
            {/* S2. Hot Leads */}
            <KpiCard
              label="Hot Leads"
              value={selectedGymKpi ? selectedGymKpi.hotLeads : (leadLoading ? '...' : filteredKpiAggregates.hotLeads)}
              icon={Flame}
              color="text-orange-400"
              valueColor="text-orange-400"
              onClick={() => toggleKpi('hot_leads')}
              active={activeKpi === 'hot_leads'}
            />
            {/* S3. Walk-ins Today */}
            <KpiCard
              label="Walk-ins Today"
              value={leadLoading ? '...' : computedWalkinCount}
              icon={Users}
              color="text-emerald-400"
              valueColor="text-emerald-400"
              onClick={() => toggleKpi('walkins')}
              active={activeKpi === 'walkins'}
            />
            {/* S4. Converted Leads */}
            {(() => {
              const converted = selectedGymKpi ? safeNum(selectedGymKpi.convertedLeads) : filteredKpiAggregates.convertedLeads;
              const allL = selectedGymKpi ? Math.max(safeNum(selectedGymKpi.allLeads), 1) : Math.max(filteredKpiAggregates.allLeads, 1);
              return (
                <KpiCard
                  label="Converted Leads"
                  value={selectedGymKpi ? converted : (leadLoading ? '...' : converted)}
                  icon={CheckCircle}
                  color="text-green-400"
                  valueColor="text-green-400"
                  sub={`${(converted / allL * 100).toFixed(1)}% rate`}
                  onClick={() => toggleKpi('converted')}
                  active={activeKpi === 'converted'}
                />
              );
            })()}
            {/* S5. C2C Connect Rate — uses gymCallStats (same source as table) */}
            {(() => {
              const cs = filteredCallStats || orgCallStats;
              const pct = selectedGymKpi ? selectedGymKpi.c2cRate : cs.c2cPct;
              const connected = selectedGymKpi ? selectedGymKpi.c2cConnected : cs.c2cConnected;
              const total = selectedGymKpi ? selectedGymKpi.c2cTotal : cs.outbound;
              return (
                <KpiCard
                  label="C2C Connect Rate"
                  value={ivrLoading ? '...' : `${pct}%`}
                  icon={PhoneCall}
                  color={pct >= 60 ? 'text-green-400' : pct >= 40 ? 'text-amber-400' : 'text-red-400'}
                  valueColor={pct >= 60 ? 'text-green-400' : pct >= 40 ? 'text-amber-400' : 'text-red-400'}
                  sub={`${connected}/${total} connected`}
                  onClick={() => toggleKpi('c2c_rate')}
                  active={activeKpi === 'c2c_rate'}
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── KPI DETAIL PANEL ─────────────────────────────────────────────── */}
      {activeKpi && (
        <KpiDetailPanel
          activeKpi={activeKpi}
          onClose={() => setActiveKpi(null)}
          leadData={leadData}
          leadLoading={leadLoading}
          kpiAggregates={kpiAggregates}
          filteredKpiAggregates={filteredKpiAggregates}
          settlLoading={settlLoading}
          ivrLoading={ivrLoading}
          todayCallCount={todayCallCount}
          todayC2cCount={todayC2cCount}
          todayC2cConnected={todayC2cConnected}
          c2cConnectRate={c2cConnectRate}
          gymRows={gymRows}
          dashReports={dashReports}
          missedInboundCount={missedInboundCount}
          c2cDispositions={c2cDispositions}
          fds={fds}
          walkinCount={computedWalkinCount}
          filteredGymCount={filteredGymRows.length}
          cardCallStats={filteredCallStats || orgCallStats}
        />
      )}

      {/* ── MAIN CONTENT ────────────────────────────────────────────────────── */}
      <div className="p-4">
        <GymPerformanceTable
          rows={filteredGymRows}
          loading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          sortConfig={gymSortConfig}
          onSort={handleGymSort}
          onSelectGym={(gymName, gymRow) => {
            if (selectedGym === gymName) {
              setSelectedGym(null);
              setSelectedGymId(null);
              setFdTodayStats([]);
              setFdSortConfig({ key: 'achievement_pct', direction: 'desc' });
              return;
            }
            setSelectedGym(gymName);
            const gymId = gymRow?.raw?.gym_id;
            setSelectedGymId(gymId);
            setFdTodayStats([]);
            if (gymId) {
              axiosInstance.get(`/wtf-data/gym/${gymId}/fd-today`)
                .then(r => setFdTodayStats(r.data?.data || []))
                .catch(() => setFdTodayStats([]));
            }
          }}
          totalGyms={gymRows.length}
          gymCallStats={gymCallStats}
          gymSalesToday={gymSalesToday}
          tlFilter={tlFilter}
          setTlFilter={setTlFilter}
          mgrFilter={mgrFilter}
          setMgrFilter={setMgrFilter}
          uniqueTLs={uniqueTLs}
          uniqueManagers={uniqueManagers}
          onClosureClick={(gymId, gymName, date) => {
            setClosureDialogGym({ gym_id: gymId, gym_name: gymName });
            setClosureDialogDate(date);
            setClosureDialogOpen(true);
          }}
          expandedGymName={selectedGym}
          expandedContent={selectedGym ? (
            <FdDrillDown
              inline
              gymName={selectedGym}
              rows={sortedFdRows}
              loading={fdLoading}
              sortConfig={fdSortConfig}
              onSort={handleFdSort}
              onBack={() => {
                setSelectedGym(null);
                setSelectedGymId(null);
                setFdTodayStats([]);
                setFdSortConfig({ key: 'achievement_pct', direction: 'desc' });
              }}
              staffDirectory={staffDirectory}
              fdCallMinutes={fdCallMinutes}
              fdTodayStats={fdTodayStats}
              gymSalesData={gymSalesToday[normalizeGymName(selectedGym)] || null}
              gymFollowupData={selectedGymId ? gymFollowups[selectedGymId] : null}
            />
          ) : null}
        />
      </div>

      {/* ── CLOSURE EXPECTED CUSTOMER DIALOG ─────────────────────────────── */}
      <CustomerListDialog
        open={closureDialogOpen}
        onOpenChange={setClosureDialogOpen}
        gym={closureDialogGym}
        date={closureDialogDate}
      />
    </div>
  );
}

// ─── KPI DETAIL PANEL ─────────────────────────────────────────────────────────

function KpiDetailPanel({ activeKpi, onClose, leadData, leadLoading, kpiAggregates, filteredKpiAggregates = {}, settlLoading, ivrLoading, todayCallCount, todayC2cCount, todayC2cConnected, c2cConnectRate, gymRows, dashReports = {}, missedInboundCount = 0, c2cDispositions = {}, fds = [], walkinCount = 0, filteredGymCount = 0, cardCallStats = {} }) {
  // Use filteredKpiAggregates (per-gym computed) as primary source; leadData as fallback
  const fka = filteredKpiAggregates;
  const conversionRate = (fka.convertedLeads / Math.max(fka.allLeads, 1) * 100).toFixed(1);

  const content = {
    followups: {
      title: 'Followups Today',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Followups added today by FDs across all gyms. This is the most important sales metric.</p>
          <div className="mt-2 text-2xl font-bold text-amber-400">{leadLoading ? '...' : fka.followupsToday.toLocaleString('en-IN')}</div>
        </div>
      ),
    },
    calls: {
      title: "Today's Calls",
      body: (
        <div>
          <p className="text-slate-300 text-sm">Call breakdown from IVR system today (matched to FDs):</p>
          <div className="mt-2 flex gap-6 text-sm">
            <div><span className="text-slate-400">Inbound:</span> <span className="text-blue-400 font-bold">{ivrLoading ? '...' : cardCallStats.inbound}</span></div>
            <div><span className="text-slate-400">Outbound (C2C):</span> <span className="text-cyan-400 font-bold">{ivrLoading ? '...' : cardCallStats.outbound}</span></div>
            <div><span className="text-slate-400">Total:</span> <span className="text-white font-bold">{ivrLoading ? '...' : cardCallStats.totalCalls}</span></div>
          </div>
          {cardCallStats.missedInbound > 0 && (
            <div className="mt-2 text-sm text-red-400">
              {cardCallStats.missedInbound} inbound calls missed ({cardCallStats.inbound > 0 ? Math.round(cardCallStats.missedInbound / cardCallStats.inbound * 100) : 0}% unanswered)
            </div>
          )}
        </div>
      ),
    },
    missed_calls: {
      title: 'Missed Calls',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Inbound calls that went unanswered today (0 duration). High missed calls = lost leads.</p>
          <div className={`mt-2 text-2xl font-bold ${cardCallStats.missedInbound > 0 ? 'text-red-400' : 'text-slate-500'}`}>
            {ivrLoading ? '...' : cardCallStats.missedInbound}
          </div>
          {cardCallStats.inbound > 0 && (
            <div className="mt-1 text-sm text-slate-400">
              {Math.round(cardCallStats.missedInbound / cardCallStats.inbound * 100)}% of {cardCallStats.inbound} inbound calls
            </div>
          )}
        </div>
      ),
    },
    leads: {
      title: "Today's Leads",
      body: (
        <div>
          <p className="text-slate-300 text-sm">New leads generated today across all gyms.</p>
          <div className="mt-2 text-2xl font-bold text-cyan-400">{leadLoading ? '...' : fka.todaysLeads.toLocaleString('en-IN')}</div>
        </div>
      ),
    },
    monthly_target: {
      title: 'Monthly Target',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Total combined monthly revenue target for {filteredGymCount || gymRows.length} gyms{filteredGymCount && filteredGymCount < gymRows.length ? ' (filtered)' : ''}.</p>
          <div className="mt-2 text-2xl font-bold text-white">{settlLoading ? '...' : formatINR(fka.monthlyTarget)}</div>
        </div>
      ),
    },
    monthly_achieved: {
      title: 'Monthly Achieved',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Total revenue achieved this month across {filteredGymCount || gymRows.length} gyms{filteredGymCount && filteredGymCount < gymRows.length ? ' (filtered)' : ''}.</p>
          <div className="mt-2 text-2xl font-bold text-green-400">{settlLoading ? '...' : formatINR(fka.monthlyAchieved)}</div>
        </div>
      ),
    },
    achievement: {
      title: 'Achievement %',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Overall achievement percentage (achieved / target).</p>
          <div className="mt-2 text-2xl font-bold">{settlLoading ? '...' : `${fka.achievementPercent}%`}</div>
          <div className="mt-1 text-xs text-slate-400">{formatINR(fka.monthlyAchieved)} / {formatINR(fka.monthlyTarget)}</div>
        </div>
      ),
    },
    sales_today: {
      title: "Today's Sales",
      body: (
        <div>
          <p className="text-slate-300 text-sm">Total sales revenue collected today{filteredGymCount && filteredGymCount < gymRows.length ? ` across ${filteredGymCount} filtered gyms` : ' across all gyms'}.</p>
          <div className="mt-2 text-2xl font-bold text-emerald-400">{formatINR(fka.salesToday || safeNum(dashReports.todaySale))}</div>
          <div className="mt-2 flex gap-4 text-sm">
            <div><span className="text-slate-400">Renewals:</span> <span className="text-green-400 font-bold">{safeNum(dashReports.renewals)}</span></div>
            <div><span className="text-slate-400">New Subs:</span> <span className="text-cyan-400 font-bold">{safeNum(dashReports.newsubs)}</span></div>
            <div><span className="text-slate-400">Check-ins:</span> <span className="text-blue-400 font-bold">{safeNum(dashReports.checkins).toLocaleString('en-IN')}</span></div>
          </div>
          <div className="mt-1 text-xs text-slate-500">MTD: {formatINR(safeNum(dashReports.totalSalesMonth))}</div>
        </div>
      ),
    },
    missed_followups: {
      title: 'Missed Followups',
      body: (
        <div>
          <p className="text-red-400 text-sm font-semibold">CRITICAL: {fka.missedFollowups.toLocaleString('en-IN')} followups were missed.</p>
          <p className="text-red-300/80 text-sm mt-1">These are leads that had scheduled followups but were not contacted. Each missed followup is a potential lost sale.</p>
          <div className="mt-2 text-3xl font-bold text-red-400 animate-pulse">{leadLoading ? '...' : fka.missedFollowups.toLocaleString('en-IN')}</div>
        </div>
      ),
    },
    hot_leads: {
      title: 'Hot Leads',
      body: (
        <div>
          <p className="text-slate-300 text-sm">{fka.hotLeads.toLocaleString('en-IN')} leads currently classified as HOT. These need immediate attention.</p>
          <div className="mt-2 text-2xl font-bold text-orange-400">{leadLoading ? '...' : fka.hotLeads.toLocaleString('en-IN')}</div>
        </div>
      ),
    },
    walkins: {
      title: 'Walk-ins Today',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Walk-in visitors to gyms today.</p>
          <div className="mt-2 text-2xl font-bold text-emerald-400">{leadLoading ? '...' : walkinCount.toLocaleString('en-IN')}</div>
        </div>
      ),
    },
    converted: {
      title: 'Converted Leads',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Total leads converted to paying members. Conversion rate: {conversionRate}%</p>
          <div className="mt-2 flex gap-6 items-end">
            <div className="text-2xl font-bold text-green-400">{leadLoading ? '...' : fka.convertedLeads.toLocaleString('en-IN')}</div>
            <div className="text-sm text-slate-400">out of {fka.allLeads.toLocaleString('en-IN')} total leads</div>
          </div>
        </div>
      ),
    },
    c2c_rate: {
      title: 'C2C Connect Rate',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Click-to-call connection rate. {c2cConnectRate.connected} out of {c2cConnectRate.total} calls connected.</p>
          <div className="mt-2 text-2xl font-bold">{ivrLoading ? '...' : `${c2cConnectRate.pct}%`}</div>
          {Object.keys(c2cDispositions).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              {Object.entries(c2cDispositions).sort((a, b) => b[1] - a[1]).map(([disp, count]) => (
                <div key={disp} className="bg-slate-800/60 rounded px-3 py-1.5">
                  <span className="text-slate-400 capitalize">{disp}:</span>{' '}
                  <span className={`font-bold ${disp === 'answered' ? 'text-green-400' : disp === 'busy' ? 'text-amber-400' : 'text-red-400'}`}>{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
    missed_inbound: {
      title: 'Missed Inbound Calls',
      body: (
        <div>
          <p className="text-slate-300 text-sm">Inbound calls from customers that went unanswered. Each missed call is a potential lost sale.</p>
          <div className="mt-2 text-2xl font-bold text-red-400">{cardCallStats.missedInbound}</div>
          <div className="mt-1 text-sm text-slate-400">
            out of {cardCallStats.inbound} total inbound calls ({cardCallStats.inbound > 0 ? Math.round(cardCallStats.missedInbound / cardCallStats.inbound * 100) : 0}% miss rate)
          </div>
        </div>
      ),
    },
  };

  const panel = content[activeKpi];
  if (!panel) return null;

  return (
    <div className="mx-4 mt-0 mb-0 overflow-hidden transition-all duration-300 ease-in-out">
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors p-1 rounded hover:bg-slate-700/50"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{panel.title}</div>
        {panel.body}
      </div>
    </div>
  );
}

// ─── LEVEL 2: GYM PERFORMANCE TABLE ────────────────────────────────────────────

function GymPerformanceTable({ rows, loading, searchTerm, onSearchChange, sortConfig, onSort, onSelectGym, totalGyms, gymCallStats = {}, gymSalesToday = {}, expandedGymName = null, expandedContent = null, tlFilter, setTlFilter, mgrFilter, setMgrFilter, uniqueTLs, uniqueManagers, onClosureClick }) {
  // Note: Filter state is now managed by parent component

  // Display rows (already filtered by parent)
  const filteredRows = rows;

  const columns = [
    { key: 'rank', label: '#', align: 'center', sortable: false },
    { key: 'gym_name', label: 'Gym Name', align: 'left' },
    { key: 'active_members', label: 'Members', align: 'center' },
    { key: 'tl_name', label: 'TL', align: 'left' },
    { key: 'manager_name', label: 'Manager', align: 'left' },
    { key: 'call_min', label: 'Call Min', align: 'center' },
    { key: 'target', label: 'Monthly Target', align: 'right' },
    { key: 'achieved', label: 'Achieved', align: 'right' },
    { key: 'remaining', label: 'Remaining', align: 'right' },
    { key: 'achievement_pct', label: 'Achv%', align: 'center' },
    { key: 'today_target', label: "Today's Target", align: 'right' },
    { key: 'deficit', label: 'Deficit', align: 'right' },
    { key: 'sales_today', label: 'Sales Today', align: 'right' },
    { key: 'closure_today', label: 'Cls Today', align: 'center' },
    { key: 'closure_tomorrow', label: 'Cls Tmrw', align: 'center' },
    { key: 'followups_today', label: 'Followups (Done/Hot)', align: 'center' },
    { key: 'fd_count', label: 'FDs', align: 'center' },
    { key: 'total_calls', label: 'Calls', align: 'center' },
    { key: 'connect_pct', label: 'Connect%', align: 'center' },
    { key: 'rev_per_fd', label: '\u20B9/FD', align: 'right' },
    { key: 'avg_per_call', label: 'Avg/Call', align: 'right' },
    { key: 'avg_per_unique', label: 'Avg/Unique', align: 'right' },
  ];

  // Summary row
  const totals = useMemo(() => {
    let target = 0, achieved = 0, remaining = 0, todayTarget = 0, deficit = 0, fdCount = 0, salesToday = 0;
    let totalActiveMembers = 0;
    let closureTodayTotal = 0, closureTmrwTotal = 0;
    let followupsTotal = 0, followupsInterested = 0;
    let totalCallMin = 0, totalCallTarget = 0, totalCallSec = 0, totalCalls = 0, totalUnique = 0;
    let totalC2cCalls = 0, totalC2cConnected = 0;
    filteredRows.forEach((r) => {
      target += r.target;
      achieved += r.achieved;
      totalActiveMembers += safeNum(r.active_members);
      remaining += r.remaining;
      todayTarget += r.today_target;
      deficit += r.deficit;
      fdCount += r.fd_count;
      salesToday += safeNum(r.sales_today);
      closureTodayTotal += safeNum(r.closure_today);
      closureTmrwTotal += safeNum(r.closure_tomorrow);
      followupsTotal += safeNum(r.followups_today);
      followupsInterested += safeNum(r.followups_interested);

      const cs = gymCallStats[r.normalized_name];
      if (cs) {
        totalCallMin += cs.total_minutes;
        totalCallTarget += cs.target_minutes;
        totalCallSec += cs.total_sec;
        totalCalls += cs.total_calls;
        totalUnique += cs.unique_customers;
        totalC2cCalls += cs.c2c_calls;
        totalC2cConnected += cs.c2c_connected;
      }
    });
    const revPerFd = fdCount > 0 ? Math.round(achieved / fdCount) : 0;
    const callTargetPct = totalCallTarget > 0 ? Math.round((totalCallMin / totalCallTarget) * 100) : 0;
    const avgPerCallSec = totalCalls > 0 ? Math.round(totalCallSec / totalCalls) : 0;
    const avgPerUniqueSec = totalUnique > 0 ? Math.round(totalCallSec / totalUnique) : 0;
    const totalConnectPct = totalC2cCalls > 0 ? Math.round((totalC2cConnected / totalC2cCalls) * 100) : 0;
    return { target, achieved, remaining, achievement_pct: achievementPct(achieved, target), todayTarget, deficit, fdCount, revPerFd, salesToday, totalActiveMembers, closureTodayTotal, closureTmrwTotal, followupsTotal, followupsInterested, totalCallMin, totalCallTarget, callTargetPct, avgPerCallSec, avgPerUniqueSec, totalCalls, totalConnectPct };
  }, [filteredRows, gymCallStats]);

  return (
    <Card className="bg-slate-900/50 border-slate-700/50 overflow-hidden">
      <CardHeader className="pb-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-400" />
              Gym Performance
              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-600 ml-1">
                {filteredRows.length} / {totalGyms}
              </Badge>
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {/* TL Filter */}
            <div className="flex items-center gap-1">
              <Filter className="w-3 h-3 text-slate-500" />
              <select
                value={tlFilter}
                onChange={(e) => setTlFilter(e.target.value)}
                className="h-7 text-xs bg-slate-800 border border-slate-700 text-slate-200 rounded px-2 outline-none focus:border-slate-500"
              >
                <option value="">All TLs</option>
                {uniqueTLs.map(tl => <option key={tl} value={tl}>{tl}</option>)}
              </select>
            </div>
            {/* Manager Filter */}
            <select
              value={mgrFilter}
              onChange={(e) => setMgrFilter(e.target.value)}
              className="h-7 text-xs bg-slate-800 border border-slate-700 text-slate-200 rounded px-2 outline-none focus:border-slate-500"
            >
              <option value="">All Managers</option>
              {uniqueManagers.map(mgr => <option key={mgr} value={mgr}>{mgr}</option>)}
            </select>
            {/* Search */}
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                placeholder="Search gyms..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="h-7 pl-8 text-xs bg-slate-800 border-slate-700 text-slate-200 placeholder:text-slate-500"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading settlement data...
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-500">
            <AlertTriangle className="w-6 h-6 mb-2 text-amber-500" />
            <span className="text-sm">No gym data available</span>
            {(searchTerm || tlFilter || mgrFilter) && <span className="text-xs mt-1">Try clearing your filters</span>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-800/80 border-b border-slate-700/50">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-2 font-semibold text-slate-400 uppercase tracking-wider ${col.sortable !== false ? 'cursor-pointer hover:text-white' : ''} select-none whitespace-nowrap text-${col.align}`}
                      onClick={() => col.sortable !== false && onSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.sortable !== false && <SortIcon column={col.key} sortConfig={sortConfig} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {filteredRows.map((row, idx) => (
                  <React.Fragment key={row.gym_name + idx}>
                  <tr
                    className={`hover:bg-slate-800/40 transition-colors ${achievementRowClass(row.achievement_pct)} ${row.fd_count > 0 ? 'cursor-pointer' : ''} ${expandedGymName === row.gym_name ? 'bg-cyan-950/30 border-l-4 !border-l-cyan-500' : ''}`}
                    onClick={() => row.fd_count > 0 && onSelectGym(row.gym_name, row)}
                  >
                    {/* Rank */}
                    <td className="px-3 py-2 text-center text-slate-500 text-[10px] font-mono">{idx + 1}</td>
                    {/* Gym Name */}
                    <td className="px-3 py-2 font-medium text-slate-200 min-w-[200px]" title={row.gym_name}>
                      <div className="flex items-center gap-1">
                        {row.achievement_pct === 0 && row.target > 0 && (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mr-1.5 flex-shrink-0" />
                        )}
                        <span className="line-clamp-2 text-[11px] leading-tight">{row.gym_name}</span>
                        {row.fd_count > 0 && (
                          expandedGymName === row.gym_name
                            ? <ChevronDown className="w-3 h-3 text-cyan-400 flex-shrink-0 ml-1" />
                            : <ChevronRight className="w-3 h-3 text-slate-600 flex-shrink-0 ml-1" />
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums text-[11px]">
                      {row.active_members != null ? (
                        <span className="text-cyan-300 font-semibold">{row.active_members.toLocaleString('en-IN')}</span>
                      ) : <span className="text-slate-600">--</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap text-[11px]">{row.tl_name}</td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap text-[11px]">{row.manager_name}</td>
                    {/* Call Min - progress bar with pace awareness */}
                    {(() => {
                      const cs = gymCallStats[row.normalized_name];
                      const actualMin = cs ? Math.round(cs.total_minutes) : 0;
                      const targetMin = cs ? cs.target_minutes : row.fd_count * 150;
                      const pctCall = targetMin > 0 ? Math.round((actualMin / targetMin) * 100) : 0;
                      const now = new Date();
                      const istHour = (now.getUTCHours() + 5 + (now.getUTCMinutes() + 30 >= 60 ? 1 : 0)) % 24;
                      const workElapsed = Math.max(0, Math.min(istHour - 9, 10));
                      const expectedMin = targetMin * (workElapsed / 10);
                      const paceRatio = expectedMin > 0 ? actualMin / expectedMin : (actualMin > 0 ? 1 : 0);
                      const barColor = paceRatio >= 0.8 ? 'bg-green-500' : paceRatio >= 0.5 ? 'bg-amber-500' : 'bg-red-500';
                      return (
                        <td className="px-3 py-2 text-center">
                          {targetMin > 0 ? (
                            <div className="min-w-[90px]">
                              <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden relative">
                                <div
                                  className={`h-full rounded-full ${barColor} transition-all`}
                                  style={{ width: `${Math.min(pctCall, 100)}%` }}
                                />
                                {workElapsed > 0 && workElapsed < 10 && (
                                  <div className="absolute top-0 bottom-0 w-0.5 bg-white/40" style={{ left: `${Math.min(workElapsed * 10, 100)}%` }} />
                                )}
                                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow">
                                  {actualMin} / {targetMin}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-slate-600 text-[10px]">--</span>
                          )}
                        </td>
                      );
                    })()}
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{formatINR(row.target)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{formatINR(row.achieved)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={row.remaining > 0 ? 'text-red-400' : 'text-green-400'}>
                        {formatINR(row.remaining)}
                      </span>
                    </td>
                    {/* Achievement with progress bar */}
                    <td className="px-3 py-2 text-center">
                      <AchievementBadge pct={row.achievement_pct} />
                      <div className="w-full h-1 bg-slate-700 rounded-full mt-1 overflow-hidden">
                        <div className={`h-full rounded-full ${row.achievement_pct >= 80 ? 'bg-green-500' : row.achievement_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                             style={{ width: `${Math.min(row.achievement_pct, 100)}%` }} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{formatINR(row.today_target)}</td>
                    {/* Deficit with checkmark for on-track */}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.deficit > 0 ? (
                        <span className="text-red-400 font-semibold">{formatINR(row.deficit)}</span>
                      ) : (
                        <span className="text-green-400" title="On track">{'\u2713'}</span>
                      )}
                    </td>
                    {/* Sales Today */}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.sales_today !== null && row.sales_today > 0 ? (
                        <span className="text-emerald-400 font-semibold">{formatINR(row.sales_today)}</span>
                      ) : row.sales_today === 0 ? (
                        <span className="text-red-400/70 text-[10px]">{'\u20B9'}0</span>
                      ) : (
                        <span className="text-slate-600 text-[10px]">--</span>
                      )}
                    </td>
                    {/* Closure Expected Today */}
                    <td className="px-3 py-2 text-center tabular-nums" title="Closure expected leads for today (click to view customers)">
                      {row.closure_today !== null && row.closure_today > 0 ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); onClosureClick && onClosureClick(row.closure_today_gym_id, row.gym_name, getISTDate()); }}
                          className="text-cyan-400 font-bold text-[11px] hover:text-cyan-300 hover:underline cursor-pointer bg-transparent border-none p-0"
                        >
                          {row.closure_today}
                        </button>
                      ) : row.closure_today === 0 ? (
                        <span className="text-slate-600 text-[10px]">0</span>
                      ) : (
                        <span className="text-slate-700 text-[10px]">—</span>
                      )}
                    </td>
                    {/* Closure Expected Tomorrow */}
                    <td className="px-3 py-2 text-center tabular-nums" title="Closure expected leads for tomorrow (click to view customers)">
                      {row.closure_tomorrow !== null && row.closure_tomorrow > 0 ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const tmrw = new Date(new Date().getTime() + (5.5 * 60 * 60 * 1000) + 86400000).toISOString().split('T')[0];
                            onClosureClick && onClosureClick(row.closure_tomorrow_gym_id, row.gym_name, tmrw);
                          }}
                          className="text-amber-400 font-bold text-[11px] hover:text-amber-300 hover:underline cursor-pointer bg-transparent border-none p-0"
                        >
                          {row.closure_tomorrow}
                        </button>
                      ) : row.closure_tomorrow === 0 ? (
                        <span className="text-slate-600 text-[10px]">0</span>
                      ) : (
                        <span className="text-slate-700 text-[10px]">—</span>
                      )}
                    </td>
                    {/* Followups Today / Hot+Semi-Hot Interested */}
                    <td className="px-3 py-2 text-center tabular-nums" title={row.followups_today !== null ? `${row.followups_today} followups done today / ${row.followups_interested || 0} hot+semi-hot interested leads` : 'No followup data'}>
                      {row.followups_today !== null ? (
                        <span className="text-[11px]">
                          <span className="text-amber-300 font-semibold">{row.followups_today}</span>
                          <span className="text-slate-500"> / <span className="text-orange-400 font-semibold">{row.followups_interested || 0}</span></span>
                        </span>
                      ) : (
                        <span className="text-slate-600 text-[10px]">--</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-bold ${row.fd_count > 0 ? 'text-cyan-400' : 'text-slate-600'}`}>
                        {row.fd_count}
                      </span>
                    </td>
                    {/* Total Calls */}
                    {(() => {
                      const cs = gymCallStats[row.normalized_name];
                      const calls = cs ? cs.total_calls : 0;
                      return (
                        <td className="px-3 py-2 text-center tabular-nums">
                          {calls > 0 ? <span className="text-blue-400 font-semibold text-[11px]">{calls}</span> : <span className="text-slate-600 text-[10px]">--</span>}
                        </td>
                      );
                    })()}
                    {/* Connect % */}
                    {(() => {
                      const cs = gymCallStats[row.normalized_name];
                      const pct = cs ? cs.connect_pct : 0;
                      const hasCalls = cs && cs.c2c_calls > 0;
                      return (
                        <td className="px-3 py-2 text-center tabular-nums">
                          {hasCalls ? (
                            <span className={`text-[11px] font-semibold ${pct >= 50 ? 'text-green-400' : pct >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
                              {pct}%
                            </span>
                          ) : <span className="text-slate-600 text-[10px]">--</span>}
                        </td>
                      );
                    })()}
                    {/* Revenue per FD */}
                    <td className="px-3 py-2 text-right text-slate-400 text-[11px] tabular-nums">
                      {row.rev_per_fd > 0 ? formatINR(row.rev_per_fd) : '--'}
                    </td>
                    {/* Avg/Call */}
                    <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">
                      {(() => {
                        const cs = gymCallStats[row.normalized_name];
                        if (!cs) return '--';
                        return formatDuration(cs.avg_per_call_sec);
                      })()}
                    </td>
                    {/* Avg/Unique */}
                    <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">
                      {(() => {
                        const cs = gymCallStats[row.normalized_name];
                        if (!cs) return '--';
                        return formatDuration(cs.avg_per_unique_sec);
                      })()}
                    </td>
                  </tr>
                  {expandedGymName === row.gym_name && expandedContent && (
                    <tr>
                      <td colSpan={columns.length} className="p-0 bg-slate-900/70 border-b-2 border-cyan-800/30">
                        {expandedContent}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Totals footer - sticky */}
              <tfoot>
                <tr className="bg-slate-800/80 border-t-2 border-slate-600 font-bold text-slate-200 sticky bottom-0 z-10">
                  {/* Rank placeholder */}
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-left uppercase tracking-wider text-[10px] text-slate-400">
                    Totals ({filteredRows.length} gyms)
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums text-cyan-300 font-semibold">{totals.totalActiveMembers.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  {/* Call Min totals - progress bar */}
                  <td className="px-3 py-2 text-center">
                    {totals.totalCallTarget > 0 ? (
                      <div className="min-w-[90px]">
                        <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden relative">
                          <div
                            className={`h-full rounded-full ${totals.callTargetPct >= 66 ? 'bg-green-500' : totals.callTargetPct >= 33 ? 'bg-amber-500' : 'bg-red-500'} transition-all`}
                            style={{ width: `${Math.min(totals.callTargetPct, 100)}%` }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow">
                            {Math.round(totals.totalCallMin)} / {totals.totalCallTarget}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-600 text-[10px]">--</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(totals.target)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(totals.achieved)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-400">{formatINR(totals.remaining)}</td>
                  <td className="px-3 py-2 text-center">
                    <AchievementBadge pct={totals.achievement_pct} />
                    <div className="w-full h-1 bg-slate-700 rounded-full mt-1 overflow-hidden">
                      <div className={`h-full rounded-full ${totals.achievement_pct >= 80 ? 'bg-green-500' : totals.achievement_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                           style={{ width: `${Math.min(totals.achievement_pct, 100)}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(totals.todayTarget)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-400">
                    {totals.deficit > 0 ? formatINR(totals.deficit) : <span className="text-green-400">{'\u2713'}</span>}
                  </td>
                  {/* Sales Today total */}
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-400">
                    {totals.salesToday > 0 ? formatINR(totals.salesToday) : '--'}
                  </td>
                  {/* Closure Today total */}
                  <td className="px-3 py-2 text-center tabular-nums text-cyan-400 font-bold text-[11px]">
                    {totals.closureTodayTotal > 0 ? totals.closureTodayTotal : '--'}
                  </td>
                  {/* Closure Tomorrow total */}
                  <td className="px-3 py-2 text-center tabular-nums text-amber-400 font-bold text-[11px]">
                    {totals.closureTmrwTotal > 0 ? totals.closureTmrwTotal : '--'}
                  </td>
                  {/* Followups total */}
                  <td className="px-3 py-2 text-center tabular-nums text-[11px]">
                    <span className="text-amber-300 font-semibold">{totals.followupsTotal}</span>
                    {totals.followupsInterested > 0 && (
                      <span className="text-slate-500"> / <span className="text-orange-400 font-semibold">{totals.followupsInterested}</span></span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-cyan-400">{totals.fdCount}</td>
                  {/* Total Calls */}
                  <td className="px-3 py-2 text-center text-blue-400 text-[11px] font-semibold tabular-nums">{totals.totalCalls > 0 ? totals.totalCalls : '--'}</td>
                  {/* Connect % */}
                  <td className="px-3 py-2 text-center text-[11px] font-semibold tabular-nums">
                    {totals.totalConnectPct > 0 ? (
                      <span className={totals.totalConnectPct >= 50 ? 'text-green-400' : totals.totalConnectPct >= 30 ? 'text-amber-400' : 'text-red-400'}>{totals.totalConnectPct}%</span>
                    ) : '--'}
                  </td>
                  {/* Revenue per FD total */}
                  <td className="px-3 py-2 text-right text-slate-400 text-[11px] tabular-nums">
                    {totals.revPerFd > 0 ? formatINR(totals.revPerFd) : '--'}
                  </td>
                  {/* Avg/Call totals */}
                  <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">
                    {totals.avgPerCallSec > 0 ? formatDuration(totals.avgPerCallSec) : '--'}
                  </td>
                  {/* Avg/Unique totals */}
                  <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">
                    {totals.avgPerUniqueSec > 0 ? formatDuration(totals.avgPerUniqueSec) : '--'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── RECORDINGS PANEL (with filters + repeat detection) ──────────────────────

function RecordingsPanel({ recordings, clientCallCounts = {} }) {
  const [filter, setFilter] = useState('all');
  const todayRecs = recordings.filter(r => r.isToday);
  const filtered = todayRecs.filter(r => {
    if (filter === 'connected') return r.disposition === 'connected';
    if (filter === 'missed') return r.disposition === 'missed';
    if (filter === 'inbound') return r.direction === 'Inbound';
    if (filter === 'outbound') return r.direction === 'Outbound';
    return true;
  });
  const filters = [
    { key: 'all', label: `All (${todayRecs.length})` },
    { key: 'connected', label: `Connected (${todayRecs.filter(r => r.disposition === 'connected').length})` },
    { key: 'missed', label: `Missed (${todayRecs.filter(r => r.disposition === 'missed').length})` },
    { key: 'inbound', label: `In (${todayRecs.filter(r => r.direction === 'Inbound').length})` },
    { key: 'outbound', label: `Out (${todayRecs.filter(r => r.direction === 'Outbound').length})` },
  ];
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">
          Call Recordings ({todayRecs.length} today)
        </div>
        <div className="flex gap-1 ml-auto">
          {filters.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-2 py-0.5 rounded text-[9px] font-semibold transition-colors ${filter === f.key ? 'bg-cyan-900/60 text-cyan-300 border border-cyan-700/50' : 'bg-slate-800/60 text-slate-500 border border-slate-700/30 hover:text-slate-300'}`}
            >{f.label}</button>
          ))}
        </div>
      </div>
      <div className="max-h-[300px] overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {filtered.map((rec, ri) => {
          const cn = normalizePhone(rec.clientNo);
          const repeatCount = cn ? (clientCallCounts[cn] || 1) : 1;
          return (
            <div key={ri} className={`bg-slate-900/80 border rounded-md px-3 py-2 flex flex-col gap-1 ${rec.disposition === 'connected' ? 'border-green-800/40' : 'border-red-800/30'}`}>
              <div className="flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-300 font-mono">{rec.clientNo ? normalizePhone(rec.clientNo) : 'Unknown'}</span>
                  {repeatCount > 2 && (
                    <span className={`px-1 py-0 rounded text-[8px] font-bold ${repeatCount >= 4 ? 'bg-red-900/50 text-red-400' : 'bg-amber-900/50 text-amber-400'}`}>
                      {repeatCount}x
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${rec.direction === 'Inbound' ? 'bg-blue-900/50 text-blue-400' : 'bg-cyan-900/50 text-cyan-400'}`}>
                    {rec.direction || (rec.type === 'c2c' ? 'Outbound' : 'Inbound')}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${rec.disposition === 'connected' ? 'bg-green-900/50 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                    {rec.disposition === 'connected' ? 'Connected' : 'Missed'}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-500">
                <span>{rec.time ? new Date(rec.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '--'}</span>
                <span className="font-mono">{rec.duration > 0 ? formatDuration(rec.duration) : '--'}</span>
              </div>
              {rec.leadSource && (
                <div className="flex items-center gap-1.5 text-[9px]">
                  <span className="px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-400 font-bold uppercase">{rec.leadSource}</span>
                  {rec.leadName && <span className="text-slate-400 truncate">{rec.leadName}</span>}
                </div>
              )}
              {rec.url && (
                <audio controls preload="none" className="w-full h-7 mt-1" style={{ filter: 'invert(1) hue-rotate(180deg) brightness(0.8)' }}>
                  <source src={rec.url} type="audio/wav" />
                </audio>
              )}
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-3 text-slate-600 text-[11px]">No recordings match filter</div>
      )}
    </div>
  );
}

// ─── LEVEL 3: FD DRILL-DOWN ───────────────────────────────────────────────────

function FdDrillDown({ gymName, rows, loading, sortConfig, onSort, onBack, staffDirectory = [], fdCallMinutes = {}, fdTodayStats = [], gymSalesData = null, gymFollowupData = null, inline = false }) {
  const [expandedFd, setExpandedFd] = useState(null);
  const [fdSearch, setFdSearch] = useState('');

  // Build staff directory lookup by normalized mobile
  const staffLookup = useMemo(() => {
    const lookup = {};
    (staffDirectory || []).forEach(s => {
      const m = normalizePhone(s.mobile);
      if (m) lookup[m] = s;
    });
    return lookup;
  }, [staffDirectory]);

  // Build fd-today lookup by normalized mobile or name
  const fdTodayLookup = useMemo(() => {
    const lookup = {};
    (fdTodayStats || []).forEach(s => {
      const m = normalizePhone(s.mobile);
      if (m) lookup[m] = s;
      // Also index by name for fallback matching
      if (s.name) lookup['name:' + s.name.toLowerCase().trim()] = s;
    });
    return lookup;
  }, [fdTodayStats]);

  // Get staff info for an FD by mobile
  const getStaffInfo = useCallback((mobile) => {
    const m = normalizePhone(mobile);
    return staffLookup[m] || null;
  }, [staffLookup]);

  // Get fd-today info for an FD
  const getFdTodayInfo = useCallback((mobile, name) => {
    const m = normalizePhone(mobile);
    if (m && fdTodayLookup[m]) return fdTodayLookup[m];
    if (name) return fdTodayLookup['name:' + name.toLowerCase().trim()] || null;
    return null;
  }, [fdTodayLookup]);

  // Get call minutes for an FD
  const getCallMinutes = useCallback((mobile) => {
    const m = normalizePhone(mobile);
    if (!m || !fdCallMinutes) return null;
    // Try exact match on last 10 digits
    for (const [key, val] of Object.entries(fdCallMinutes)) {
      if (normalizePhone(key) === m) return val;
    }
    return null;
  }, [fdCallMinutes]);

  // Handle FD row click - toggle activity drill-down
  const handleFdClick = useCallback((row) => {
    const fdKey = row.fd_name + ':' + row.mobile;
    setExpandedFd(prev => prev === fdKey ? null : fdKey);
  }, []);

  // Filter FD rows by search (name or mobile)
  const displayRows = useMemo(() => {
    if (!fdSearch) return rows;
    const q = fdSearch.toLowerCase();
    return rows.filter(r =>
      (r.fd_name && r.fd_name.toLowerCase().includes(q)) ||
      (r.mobile && r.mobile.toLowerCase().includes(q))
    );
  }, [rows, fdSearch]);

  const columns = [
    { key: 'fd_name', label: 'FD Name', align: 'left' },
    { key: 'doj', label: 'DOJ', align: 'center', sortable: false },
    { key: 'tl_name', label: 'TL', align: 'left' },
    { key: 'manager_name', label: 'Manager', align: 'left' },
    { key: 'mobile', label: 'Mobile', align: 'left' },
    { key: 'target', label: 'Target', align: 'right' },
    { key: 'achieved', label: 'Achieved', align: 'right' },
    { key: 'remaining', label: 'Remaining', align: 'right' },
    { key: 'achievement_pct', label: 'Achv%', align: 'center' },
    { key: 'today_target', label: "Today's Target", align: 'right' },
    { key: 'deficit', label: 'Deficit', align: 'right' },
    { key: 'today_sales', label: "Today's Sales", align: 'right', sortable: false },
    { key: 'today_sales_target', label: "Today's Tgt", align: 'right', sortable: false },
    { key: 'call_minutes', label: 'Minutes (150)', align: 'center', sortable: false },
    { key: 'ivr_avg', label: 'Avg/Call', align: 'right' },
    { key: 'ivr_avg_unique', label: 'Avg/Unique', align: 'right' },
    { key: 'ivr_unique', label: 'Unique', align: 'center' },
    { key: 'recordings', label: 'Calls', align: 'center' },
  ];

  // FD totals (computed from displayRows for filtered accuracy)
  const totals = useMemo(() => {
    let target = 0, achieved = 0, remaining = 0, todayTarget = 0, deficit = 0;
    let ivrCalls = 0, ivrUnique = 0, ivrDuration = 0;
    displayRows.forEach((r) => {
      target += r.target;
      achieved += r.achieved;
      remaining += r.remaining;
      todayTarget += r.today_target;
      deficit += r.deficit;
      ivrCalls += r.ivr_calls || 0;
      ivrUnique += r.ivr_unique || 0;
      ivrDuration += r.ivr_duration || 0;
    });
    const totalRecordings = displayRows.reduce((sum, r) => sum + (r.recordings ? r.recordings.length : 0), 0);
    return {
      target, achieved, remaining, achievement_pct: achievementPct(achieved, target), todayTarget, deficit,
      ivrCalls, ivrUnique, ivrDuration,
      ivrAvg: ivrCalls > 0 ? Math.round(ivrDuration / ivrCalls) : 0,
      ivrAvgUnique: ivrUnique > 0 ? Math.round(ivrDuration / ivrUnique) : 0,
      totalRecordings,
    };
  }, [displayRows]);

  // Compute FD-level today's sales sum and unattributed gap
  const fdSalesTotals = useMemo(() => {
    let fdSum = 0;
    displayRows.forEach(r => {
      const todayInfo = getFdTodayInfo(r.mobile, r.fd_name);
      if (todayInfo) fdSum += safeNum(todayInfo.sales_completed_today);
    });
    const gymTotal = gymSalesData ? gymSalesData.total : 0;
    const unattributed = gymTotal - fdSum;
    return { fdSum, gymTotal, unattributed };
  }, [displayRows, gymSalesData, getFdTodayInfo]);

  return (
    <Card className={inline ? "bg-transparent border-0 shadow-none rounded-none overflow-hidden" : "bg-slate-900/50 border-slate-700/50 overflow-hidden"}>
      <CardHeader className={inline ? "pb-2 pt-2 px-3 border-b border-slate-700/30 bg-slate-800/40" : "pb-3 border-b border-slate-700/50"}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!inline && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="h-7 px-2 text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            {!inline && (
              <div className="flex items-center gap-1 text-xs">
                <span
                  className="text-slate-500 hover:text-slate-300 cursor-pointer"
                  onClick={onBack}
                >
                  All Gyms
                </span>
                <ChevronRight className="w-3 h-3 text-slate-600" />
                <span className="text-white font-semibold">{gymName}</span>
              </div>
            )}
            {inline && (
              <span className="text-[10px] text-cyan-400 uppercase font-bold tracking-wider">FD Breakdown</span>
            )}
            {gymSalesData && (
              <div className="flex items-center gap-2 ml-3" title={`Renewals: ${formatINR(gymSalesData.renewals * 1000 || 0)} | New: ${formatINR(gymSalesData.newSubs * 1000 || 0)} | Addon PT: ${formatINR(gymSalesData.addonPt * 1000 || 0)} | Recurring: ${formatINR(gymSalesData.recurring * 1000 || 0)}`}>
                <span className="text-[10px] text-slate-500 uppercase">Today:</span>
                <span className={`text-xs font-bold ${gymSalesData.total > 0 ? 'text-emerald-400' : 'text-red-400/70'}`}>
                  {formatINR(gymSalesData.total)}
                </span>
                {gymSalesData.renewals > 0 && (
                  <span className="text-[9px] text-slate-500">({gymSalesData.renewals} renew, {gymSalesData.newSubs} new)</span>
                )}
                {fdSalesTotals.unattributed > 0 && (
                  <span className="text-[9px] text-amber-500" title="Revenue recorded at gym level but not attributed to any FD (e.g., manager sales, addon PT, auto-payments)">
                    +{formatINR(fdSalesTotals.unattributed)} other
                  </span>
                )}
              </div>
            )}
            {gymFollowupData && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-slate-700">
                <span className="text-[10px] text-slate-500 uppercase">Followups:</span>
                <span className="text-xs font-bold text-amber-300">{gymFollowupData.todaysFollowups}</span>
                <span className="text-[9px] text-slate-500">
                  (<span className="text-red-400">{gymFollowupData.hotLeads} hot</span>
                  {gymFollowupData.semiHotLeads > 0 && <>, <span className="text-orange-400">{gymFollowupData.semiHotLeads} semi</span></>}
                  {gymFollowupData.warmLeads > 0 && <>, <span className="text-yellow-400">{gymFollowupData.warmLeads} warm</span></>}
                  {gymFollowupData.coldLeads > 0 && <>, <span className="text-blue-400">{gymFollowupData.coldLeads} cold</span></>}
                  {gymFollowupData.missedFollowups > 0 && <>, <span className="text-red-500">{gymFollowupData.missedFollowups} missed</span></>})
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                placeholder="Search FDs..."
                value={fdSearch}
                onChange={(e) => setFdSearch(e.target.value)}
                className="h-7 pl-8 text-xs bg-slate-800 border-slate-700 text-slate-200 placeholder:text-slate-500"
              />
            </div>
            <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-800">
              {displayRows.length} / {rows.length} FD{rows.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-slate-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading FD data...
          </div>
        ) : displayRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-500">
            <AlertTriangle className="w-6 h-6 mb-2 text-amber-500" />
            <span className="text-sm">No FD data{fdSearch ? ' matching search' : ' for this gym'}</span>
            {fdSearch && <span className="text-xs mt-1">Try clearing your search</span>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-800/80 border-b border-slate-700/50">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`px-3 py-2 font-semibold text-slate-400 uppercase tracking-wider ${col.sortable !== false ? 'cursor-pointer hover:text-white' : ''} select-none whitespace-nowrap text-${col.align}`}
                      onClick={() => col.sortable !== false && onSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.sortable !== false && <SortIcon column={col.key} sortConfig={sortConfig} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {displayRows.map((row, idx) => {
                  const staffInfo = getStaffInfo(row.mobile);
                  const dojDate = staffInfo?.joining_date;
                  const dojDays = daysSince(dojDate);
                  const dojColor = dojDays !== null && dojDays < 30 ? 'text-blue-400' : dojDays !== null && dojDays > 90 ? 'text-slate-500' : 'text-slate-300';

                  const todayInfo = getFdTodayInfo(row.mobile, row.fd_name);
                  const todaySales = todayInfo ? safeNum(todayInfo.sales_completed_today) : null;
                  const todaySalesTarget = todayInfo ? safeNum(todayInfo.sales_target_today) : null;

                  const callMins = getCallMinutes(row.mobile);
                  const totalMinutes = callMins ? safeNum(callMins.total_minutes) : null;
                  const minutesPct = totalMinutes !== null ? Math.round((totalMinutes / 150) * 100) : null;
                  const minutesColor = totalMinutes !== null ? (totalMinutes >= 100 ? 'bg-green-500' : totalMinutes >= 50 ? 'bg-amber-500' : 'bg-red-500') : 'bg-slate-600';

                  const avgPerCall = callMins ? safeNum(callMins.avg_per_call) : row.ivr_avg;
                  const avgPerUnique = callMins ? safeNum(callMins.avg_per_unique) : row.ivr_avg_unique;
                  const uniqueCustomers = callMins ? safeNum(callMins.unique_customers) : row.ivr_unique;

                  const fdKey = row.fd_name + ':' + row.mobile;
                  const isExpanded = expandedFd === fdKey;

                  return (
                    <React.Fragment key={row.fd_name + idx}>
                      <tr
                        className={`hover:bg-slate-800/40 transition-colors cursor-pointer ${achievementRowClass(row.achievement_pct)}`}
                        onClick={() => handleFdClick(row)}
                      >
                        {/* FD Name */}
                        <td className="px-3 py-2 font-medium text-slate-200 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span>{row.fd_name}</span>
                            <ChevronRight className={`w-3 h-3 text-slate-600 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </div>
                        </td>
                        {/* DOJ */}
                        <td className="px-3 py-2 text-center whitespace-nowrap">
                          {dojDate ? (
                            <span className={`text-[10px] ${dojColor}`}>
                              {formatDOJ(dojDate)}
                              {dojDays !== null && (
                                <span className={`ml-1 inline-block text-[9px] px-1 py-0 rounded ${dojDays < 30 ? 'bg-blue-900/50 text-blue-300' : dojDays > 90 ? 'bg-slate-700/50 text-slate-400' : 'bg-slate-700/50 text-slate-300'}`}>
                                  {dojDays}d
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-slate-600 text-[10px]">--</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-400 whitespace-nowrap text-[11px]">{row.tl_name}</td>
                        <td className="px-3 py-2 text-slate-400 whitespace-nowrap text-[11px]">{row.manager_name}</td>
                        <td className="px-3 py-2 text-slate-400 whitespace-nowrap font-mono text-[11px]">{row.mobile}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{formatINR(row.target)}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{formatINR(row.achieved)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={row.remaining > 0 ? 'text-red-400' : 'text-green-400'}>
                            {formatINR(row.remaining)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <AchievementBadge pct={row.achievement_pct} />
                        </td>
                        {/* Today's Target (from settlement) */}
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{formatINR(row.today_target)}</td>
                        {/* Deficit */}
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={row.deficit > 0 ? 'text-red-400 font-semibold' : 'text-green-400'}>
                            {row.deficit > 0 ? formatINR(row.deficit) : '--'}
                          </span>
                        </td>
                        {/* Today's Sales (from fd-today API) */}
                        <td className="px-3 py-2 text-right tabular-nums">
                          {todaySales !== null ? (
                            <span className={todaySales > 0 ? 'text-green-400 font-semibold' : 'text-red-400'}>
                              {formatINR(todaySales)}
                            </span>
                          ) : (
                            <span className="text-slate-600 text-[10px]">--</span>
                          )}
                        </td>
                        {/* Today's Target */}
                        <td className="px-3 py-2 text-right tabular-nums text-slate-400">
                          {todaySalesTarget !== null ? formatINR(todaySalesTarget) : <span className="text-slate-600 text-[10px]">--</span>}
                        </td>
                        {/* Minutes (150-min target) - mini progress bar with pace */}
                        <td className="px-3 py-2 text-center">
                          {totalMinutes !== null ? (() => {
                            const now = new Date();
                            const istHour = (now.getUTCHours() + 5 + (now.getUTCMinutes() + 30 >= 60 ? 1 : 0)) % 24;
                            const workElapsed = Math.max(0, Math.min(istHour - 9, 10));
                            const expectedMin = 150 * (workElapsed / 10);
                            const paceRatio = expectedMin > 0 ? totalMinutes / expectedMin : (totalMinutes > 0 ? 1 : 0);
                            const paceColor = paceRatio >= 0.8 ? 'bg-green-500' : paceRatio >= 0.5 ? 'bg-amber-500' : 'bg-red-500';
                            return (
                              <div className="min-w-[80px]">
                                <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden relative">
                                  <div className={`h-full rounded-full ${paceColor} transition-all`} style={{ width: `${Math.min(minutesPct, 100)}%` }} />
                                  {workElapsed > 0 && workElapsed < 10 && (
                                    <div className="absolute top-0 bottom-0 w-0.5 bg-white/40" style={{ left: `${workElapsed * 10}%` }} />
                                  )}
                                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow">{Math.round(totalMinutes)}/150</span>
                                </div>
                              </div>
                            );
                          })() : (
                            <span className="text-slate-600 text-[10px]">--</span>
                          )}
                        </td>
                        {/* Avg/Call */}
                        <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">
                          {callMins ? formatDuration(avgPerCall) : (avgPerCall > 0 ? formatDuration(avgPerCall) : '--')}
                        </td>
                        {/* Avg/Unique */}
                        <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">
                          {callMins ? formatDuration(avgPerUnique) : (avgPerUnique > 0 ? formatDuration(avgPerUnique) : '--')}
                        </td>
                        {/* Unique customers */}
                        <td className="px-3 py-2 text-center font-mono text-blue-400">{uniqueCustomers || 0}</td>
                        {/* Recordings */}
                        <td className="px-3 py-2 text-center">
                          {row.recordings && row.recordings.length > 0 ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-[10px] text-blue-400 hover:text-blue-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedFd(isExpanded ? null : fdKey);
                              }}
                            >
                              <Headphones className="w-3 h-3 mr-1" />
                              {row.recordings.length}
                            </Button>
                          ) : (
                            <span className="text-slate-600 text-[10px]">--</span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded: FD Activity Dashboard */}
                      {isExpanded && (
                        <tr className="bg-slate-800/40">
                          <td colSpan={columns.length} className="px-4 py-3">
                            <div className="space-y-3">
                              <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">
                                Activity — {row.fd_name}
                              </div>
                              {/* FD KPI mini-cards */}
                              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                                {(() => {
                                  const cm = callMins;
                                  const totalMin = cm ? safeNum(cm.total_minutes) : (row.connectedDuration ? Math.round(safeNum(row.connectedDuration) / 60 * 10) / 10 : 0);
                                  const minPct = Math.round((totalMin / 150) * 100);
                                  const c2cCount = cm ? safeNum(cm.c2c_calls) : (row.ivr_c2c || 0);
                                  const c2cConn = cm ? safeNum(cm.c2c_connected) : (row.ivr_connected || 0);
                                  const incCount = cm ? safeNum(cm.incoming_calls) : ((row.ivr_calls || 0) - (row.ivr_c2c || 0));
                                  const connectPct = c2cCount > 0 ? Math.round((c2cConn / c2cCount) * 100) : 0;
                                  // Time since last call
                                  const todayRecs = (row.recordings || []).filter(r => r.isToday);
                                  const sortedRecs = [...todayRecs].sort((a, b) => (b.time || '').localeCompare(a.time || ''));
                                  const lastCallTime = sortedRecs.length > 0 && sortedRecs[0].time ? new Date(sortedRecs[0].time) : null;
                                  const msSinceLast = lastCallTime ? Date.now() - lastCallTime.getTime() : null;
                                  const minSinceLast = msSinceLast !== null ? Math.round(msSinceLast / 60000) : null;
                                  const lastCallText = minSinceLast !== null ? (minSinceLast < 60 ? `${minSinceLast}m ago` : `${Math.floor(minSinceLast / 60)}h ${minSinceLast % 60}m ago`) : '--';
                                  const lastCallColor = minSinceLast !== null ? (minSinceLast < 30 ? 'text-green-400' : minSinceLast < 120 ? 'text-amber-400' : 'text-red-400') : 'text-slate-500';
                                  return (<>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Talk Minutes</div>
                                      <div className={`text-sm font-bold tabular-nums ${totalMin >= 100 ? 'text-green-400' : totalMin >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                                        {Math.round(totalMin)}m
                                      </div>
                                      <div className="text-[9px] text-slate-600">{minPct}% of 150m</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">C2C Calls</div>
                                      <div className="text-sm font-bold tabular-nums text-cyan-400">{c2cCount}</div>
                                      <div className="text-[9px] text-slate-600">{c2cConn} connected</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Connect %</div>
                                      <div className={`text-sm font-bold tabular-nums ${connectPct >= 50 ? 'text-green-400' : connectPct >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
                                        {c2cCount > 0 ? `${connectPct}%` : '--'}
                                      </div>
                                      <div className="text-[9px] text-slate-600">{c2cConn}/{c2cCount}</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Incoming</div>
                                      <div className="text-sm font-bold tabular-nums text-blue-400">{incCount}</div>
                                      <div className="text-[9px] text-slate-600">{uniqueCustomers || 0} unique</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Avg/Call</div>
                                      <div className="text-sm font-bold tabular-nums text-slate-300">{avgPerCall > 0 ? formatDuration(avgPerCall) : '--'}</div>
                                      <div className="text-[9px] text-slate-600">per call</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Last Call</div>
                                      <div className={`text-sm font-bold tabular-nums ${lastCallColor}`}>{lastCallText}</div>
                                      <div className="text-[9px] text-slate-600">{lastCallTime ? lastCallTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Today Sales</div>
                                      <div className={`text-sm font-bold tabular-nums ${todaySales > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                                        {todaySales !== null ? formatINR(todaySales) : '--'}
                                      </div>
                                      <div className="text-[9px] text-slate-600">{todaySalesTarget ? `Tgt: ${formatINR(todaySalesTarget)}` : ''}</div>
                                    </div>
                                    <div className="bg-slate-900/60 border border-slate-700/40 rounded px-2.5 py-2">
                                      <div className="text-[9px] text-slate-500 uppercase">Remaining</div>
                                      <div className="text-sm font-bold tabular-nums text-amber-400">{cm ? `${Math.round(safeNum(cm.remaining_minutes))}m` : '--'}</div>
                                      <div className="text-[9px] text-slate-600">of 150m target</div>
                                    </div>
                                  </>);
                                })()}
                              </div>
                              {/* Call Recordings inline — all recordings with filters */}
                              {row.recordings && row.recordings.length > 0 && (() => {
                                // Repeat call detection: count calls per client
                                const clientCallCounts = {};
                                const todayRecs = row.recordings.filter(r => r.isToday);
                                todayRecs.forEach(r => {
                                  const cn = normalizePhone(r.clientNo);
                                  if (cn) clientCallCounts[cn] = (clientCallCounts[cn] || 0) + 1;
                                });
                                return (
                                  <RecordingsPanel recordings={row.recordings} clientCallCounts={clientCallCounts} />
                                );
                              })()}
                              {/* No data at all */}
                              {!callMins && (!row.recordings || row.recordings.length === 0) && !todayInfo && (
                                <div className="text-center py-2 text-slate-600 text-[11px]">
                                  No activity data available yet today.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {/* Totals footer */}
              <tfoot>
                <tr className="bg-slate-800/80 border-t-2 border-slate-600 font-bold text-slate-200">
                  {/* 1. FD Name */}
                  <td className="px-3 py-2 text-left uppercase tracking-wider text-[10px] text-slate-400">
                    Totals ({displayRows.length} FDs)
                  </td>
                  {/* 2. DOJ */}
                  <td className="px-3 py-2"></td>
                  {/* 3. TL */}
                  <td className="px-3 py-2"></td>
                  {/* 4. Manager */}
                  <td className="px-3 py-2"></td>
                  {/* 5. Mobile */}
                  <td className="px-3 py-2"></td>
                  {/* 6. Target */}
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(totals.target)}</td>
                  {/* 7. Achieved */}
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(totals.achieved)}</td>
                  {/* 8. Remaining */}
                  <td className="px-3 py-2 text-right tabular-nums text-red-400">{formatINR(totals.remaining)}</td>
                  {/* 9. Achv% */}
                  <td className="px-3 py-2 text-center">
                    <AchievementBadge pct={totals.achievement_pct} />
                  </td>
                  {/* 10. Today's Target */}
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(totals.todayTarget)}</td>
                  {/* 11. Deficit */}
                  <td className="px-3 py-2 text-right tabular-nums text-red-400">
                    {totals.deficit > 0 ? formatINR(totals.deficit) : '--'}
                  </td>
                  {/* 12. Today's Sales */}
                  <td className="px-3 py-2"></td>
                  {/* 13. Today's Tgt */}
                  <td className="px-3 py-2"></td>
                  {/* 14. Minutes (150) */}
                  <td className="px-3 py-2"></td>
                  {/* 15. Avg/Call */}
                  <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">{totals.ivrAvg > 0 ? formatDuration(totals.ivrAvg) : '--'}</td>
                  {/* 16. Avg/Unique */}
                  <td className="px-3 py-2 text-right text-[11px] tabular-nums text-slate-300">{totals.ivrAvgUnique > 0 ? formatDuration(totals.ivrAvgUnique) : '--'}</td>
                  {/* 17. Unique */}
                  <td className="px-3 py-2 text-center font-mono text-blue-400">{totals.ivrUnique}</td>
                  {/* 18. Recordings */}
                  <td className="px-3 py-2 text-center text-slate-500 text-[10px]">{totals.totalRecordings}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
